import { usePublicProjectReader, projectReaderGet, publicQueryKey } from '@/lib/public-project-reader';
import React, { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import {
    flexRender,
    getCoreRowModel,
    getSortedRowModel,
    getFilteredRowModel,
    useReactTable,
    type ColumnDef,
    type SortingState,
    type ColumnFiltersState,
    type VisibilityState,
    type ColumnOrderState,
} from '@tanstack/react-table';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import {
    ChevronDown,
    ChevronRight,
    ChevronUp,
    MoreHorizontal,
    Pencil,
    Trash2,
    Calendar,
    User,
    MessageSquare,
    Paperclip,
    Circle,
    Loader2,
    Eye,
    CheckCircle2,
    XCircle,
    Minus,
    ArrowDown,
    ArrowRight,
    ArrowUp,
    AlertTriangle,
    Plus,
    GripVertical,
    Columns,
    EyeOff,
    Check,
    X,
    Settings,
    type LucideIcon,
} from 'lucide-react';
import { apiGet } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
    DropdownMenuCheckboxItem,
} from '@/components/ui/dropdown-menu';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@/components/ui/popover';
import type { Task, TaskStatus, TaskPriority, CustomField, ProjectMember } from '@/lib/schemas';
import { cn } from '@/lib/utils';
import { QuickAddTaskPopover } from './quick-add-task-popover';
import { useI18n } from '@/lib/i18n';

// ============================================
// STATUS & PRIORITY CONFIGS
// ============================================

// Keys for status - labels retrieved via getStatusLabel with t function
export const statusConfig: Record<TaskStatus, { key: TaskStatus; variant: 'todo' | 'inProgress' | 'inReview' | 'done' | 'cancelled'; icon: LucideIcon; color: string }> = {
    TODO: { key: 'TODO', variant: 'todo', icon: Circle, color: 'text-gray-400' },
    IN_PROGRESS: { key: 'IN_PROGRESS', variant: 'inProgress', icon: Loader2, color: 'text-blue-500' },
    IN_REVIEW: { key: 'IN_REVIEW', variant: 'inReview', icon: Eye, color: 'text-purple-500' },
    DONE: { key: 'DONE', variant: 'done', icon: CheckCircle2, color: 'text-green-500' },
    CANCELLED: { key: 'CANCELLED', variant: 'cancelled', icon: XCircle, color: 'text-red-500' },
};

export const priorityConfig: Record<TaskPriority, { key: TaskPriority; variant: 'none' | 'low' | 'medium' | 'high' | 'urgent'; icon: LucideIcon; color: string }> = {
    NONE: { key: 'NONE', variant: 'none', icon: Minus, color: 'text-gray-400' },
    LOW: { key: 'LOW', variant: 'low', icon: ArrowDown, color: 'text-gray-500' },
    MEDIUM: { key: 'MEDIUM', variant: 'medium', icon: ArrowRight, color: 'text-yellow-500' },
    HIGH: { key: 'HIGH', variant: 'high', icon: ArrowUp, color: 'text-orange-500' },
    URGENT: { key: 'URGENT', variant: 'urgent', icon: AlertTriangle, color: 'text-red-500' },
};

// Helper to get translated status label
export function getStatusLabel(status: TaskStatus, t: (key: any) => string): string {
    const labelMap: Record<TaskStatus, string> = {
        TODO: t('status.todo'),
        IN_PROGRESS: t('status.inProgress'),
        IN_REVIEW: t('status.inReview'),
        DONE: t('status.done'),
        CANCELLED: t('status.cancelled'),
    };
    return labelMap[status];
}

// Helper to get translated priority label
export function getPriorityLabel(priority: TaskPriority, t: (key: any) => string): string {
    const labelMap: Record<TaskPriority, string> = {
        NONE: t('priority.none'),
        LOW: t('priority.low'),
        MEDIUM: t('priority.medium'),
        HIGH: t('priority.high'),
        URGENT: t('priority.urgent'),
    };
    return labelMap[priority];
}

// ============================================
// HELPERS
// ============================================

function getInitials(name: string) {
    return name
        .split(' ')
        .map((n) => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);
}

// Local SprintDef parser — mirrors `parseSprintOptions` in
// custom-field-manager.tsx / task-detail-panel.tsx. Kept local so
// the table bundle doesn't pull the field manager in.
interface SprintOption {
    id: string;
    name: string;
    startDate: string;
    endDate: string;
    color?: string;
}

function parseSprintOptionsForField(raw: unknown): SprintOption[] {
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return [];
    const obj = raw as Record<string, unknown>;
    const arr = obj.sprints;
    if (!Array.isArray(arr)) return [];
    return arr
        .map((entry): SprintOption | null => {
            if (!entry || typeof entry !== 'object') return null;
            const e = entry as Record<string, unknown>;
            if (typeof e.id !== 'string') return null;
            if (typeof e.name !== 'string') return null;
            if (typeof e.startDate !== 'string') return null;
            if (typeof e.endDate !== 'string') return null;
            const color = typeof e.color === 'string' ? e.color : undefined;
            return { id: e.id, name: e.name, startDate: e.startDate, endDate: e.endDate, color };
        })
        .filter((s): s is SprintOption => s !== null);
}

function formatDate(dateString: string | null) {
    if (!dateString) return null;
    const date = new Date(dateString);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    if (date.toDateString() === today.toDateString()) {
        return 'Today';
    }
    if (date.toDateString() === tomorrow.toDateString()) {
        return 'Tomorrow';
    }

    return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: date.getFullYear() !== today.getFullYear() ? 'numeric' : undefined,
    });
}

function isOverdue(dateString: string | null) {
    if (!dateString) return false;
    const date = new Date(dateString);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
}

// ============================================
// SUBTASK HOOK
// ============================================

function useSubtasks(projectId: string, parentId: string | null, enabled: boolean) {
    const reader = usePublicProjectReader();
    return useQuery({
        queryKey: publicQueryKey(reader, ['tasks', 'list', projectId, { parentId }]),
        queryFn: async () => {
            const data = await projectReaderGet<{ tasks: Task[]; total: number }>(reader,
                `/projects/${projectId}/tasks?parentId=${parentId}`
            );
            return data;
        },
        enabled: enabled && !!projectId && !!parentId,
        staleTime: 30 * 1000,
    });
}

// ============================================
// SUBTASK TITLE CELL COMPONENT
// ============================================

interface SubtaskTitleCellProps {
    subtask: Task;
    depth: number;
    isExpanded: boolean;
    canEdit: boolean;
    onToggleExpand: () => void;
    onTitleChange?: (taskId: string, title: string) => Promise<void>;
    onTaskClick?: (task: Task) => void;
    onContextMenu: (e: React.MouseEvent) => void;
}

function SubtaskTitleCell({
    subtask,
    depth,
    isExpanded,
    canEdit,
    onToggleExpand,
    onTitleChange,
    onTaskClick,
    onContextMenu,
}: SubtaskTitleCellProps) {
    const [isEditing, setIsEditing] = useState(false);
    const [editValue, setEditValue] = useState(subtask.title);
    const [isSaving, setIsSaving] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);
    const clickTimerRef = useRef<NodeJS.Timeout | null>(null);

    const handleSaveTitle = async () => {
        if (editValue.trim() === subtask.title || !editValue.trim()) {
            setEditValue(subtask.title);
            setIsEditing(false);
            return;
        }
        if (!onTitleChange) {
            setIsEditing(false);
            return;
        }
        setIsSaving(true);
        try {
            await onTitleChange(subtask.id, editValue.trim());
            setIsEditing(false);
        } catch (error) {
            console.error('Failed to update title:', error);
            setEditValue(subtask.title);
            setIsEditing(false);
        } finally {
            setIsSaving(false);
        }
    };

    useEffect(() => {
        if (isEditing && inputRef.current) {
            inputRef.current.focus();
            inputRef.current.select();
        }
    }, [isEditing]);

    return (
        <div className="flex items-start gap-2" style={{ paddingLeft: `${depth * 16}px` }}>
            {subtask.subtaskCount > 0 ? (
                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        onToggleExpand();
                    }}
                    className="p-1 hover:bg-surface-hover rounded transition-transform"
                >
                    {isExpanded ? (
                        <ChevronDown className="h-4 w-4 text-foreground-muted" />
                    ) : (
                        <ChevronRight className="h-4 w-4 text-foreground-muted" />
                    )}
                </button>
            ) : (
                <div className="w-6" />
            )}
            <div className="flex-1 min-w-0">
                {isEditing ? (
                    <div className="flex items-center gap-1">
                        <Input
                            ref={inputRef}
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    handleSaveTitle();
                                } else if (e.key === 'Escape') {
                                    setEditValue(subtask.title);
                                    setIsEditing(false);
                                }
                            }}
                            onBlur={handleSaveTitle}
                            className="h-7 text-sm font-medium"
                            disabled={isSaving}
                        />
                        <Button
                            size="icon"
                            variant="ghost"
                            className="h-7 w-7 shrink-0"
                            onClick={handleSaveTitle}
                            disabled={isSaving}
                        >
                            {isSaving ? (
                                <Loader2 className="h-3 w-3 animate-spin" />
                            ) : (
                                <Check className="h-3 w-3 text-green-500" />
                            )}
                        </Button>
                        <Button
                            size="icon"
                            variant="ghost"
                            className="h-7 w-7 shrink-0"
                            onClick={() => {
                                setEditValue(subtask.title);
                                setIsEditing(false);
                            }}
                            disabled={isSaving}
                        >
                            <X className="h-3 w-3 text-red-500" />
                        </Button>
                    </div>
                ) : (
                    <button
                        onClick={(e) => {
                            // Use a timer to distinguish between single and double click
                            if (clickTimerRef.current) {
                                clearTimeout(clickTimerRef.current);
                            }
                            clickTimerRef.current = setTimeout(() => {
                                // Single click: open task detail panel
                                if (onTaskClick) {
                                    onTaskClick(subtask);
                                }
                            }, 200);
                        }}
                        onDoubleClick={(e) => {
                            // Clear the single click timer
                            if (clickTimerRef.current) {
                                clearTimeout(clickTimerRef.current);
                                clickTimerRef.current = null;
                            }
                            e.preventDefault();
                            e.stopPropagation();
                            // Double click: enable editing mode
                            if (onTitleChange && canEdit) {
                                setEditValue(subtask.title);
                                setIsEditing(true);
                            }
                        }}
                        onContextMenu={onContextMenu}
                        className="text-left font-medium text-foreground hover:text-primary block w-full whitespace-normal break-words"
                        title={subtask.title}
                    >
                        {subtask.title}
                    </button>
                )}
            </div>
            {/* Indicators - show count badges */}
            {!isEditing && (
                <div className="flex items-center gap-1 text-foreground-muted">
                    {subtask.subtaskCount > 0 && (
                        <span className="text-xs flex items-center gap-0.5">
                            <ChevronRight className="h-3 w-3" />
                            {subtask.subtaskCount}
                        </span>
                    )}
                    {subtask.commentCount > 0 && (
                        <span className="text-xs flex items-center gap-0.5">
                            <MessageSquare className="h-3 w-3" />
                            {subtask.commentCount}
                        </span>
                    )}
                    {subtask.attachmentCount > 0 && (
                        <span className="text-xs flex items-center gap-0.5">
                            <Paperclip className="h-3 w-3" />
                            {subtask.attachmentCount}
                        </span>
                    )}
                </div>
            )}
        </div>
    );
}

// ============================================
// FILTER DEFAULTS
// ============================================

interface FilterDefaults {
    status?: TaskStatus;
    priority?: TaskPriority;
    assigneeId?: string;
    // t43 (R13): accepts the new per-type filter shape OR the legacy
    // option-id-list. The runtime consumer is responsible for
    // normalising to one or the other.
    customFields?: Record<string, unknown>;
}

// ============================================
// SUBTASK ROWS COMPONENT
// ============================================

interface SubtaskRowsProps {
    projectId: string;
    parentTaskId: string;
    isExpanded: boolean;
    depth: number;
    columnCount: number;
    onEditTask: (task: Task) => void;
    onDeleteTask: (taskId: string) => void;
    onStatusChange: (taskId: string, status: TaskStatus) => void;
    onPriorityChange: (taskId: string, priority: TaskPriority) => void;
    onTaskClick?: (task: Task) => void;
    projectMembers: Array<{ userId: string; user: { id: string; name: string; avatarUrl: string | null } }>;
    onAssigneeChange?: (taskId: string, assigneeIds: string[]) => void;
    expandedTasks: Set<string>;
    setExpandedTasks: React.Dispatch<React.SetStateAction<Set<string>>>;
    canEdit: boolean;
    onInlineCreate?: (title: string, parentId?: string) => Promise<void>;
    onQuickCreate?: (data: { title: string; status: TaskStatus; priority: TaskPriority; dueDate: string | null; assigneeIds: string[]; parentId?: string; customFieldValues?: Record<string, unknown> }) => Promise<void>;
    customFields?: CustomField[];
    onTitleChange?: (taskId: string, title: string) => Promise<void>;
    filterDefaults?: FilterDefaults;
}

function SubtaskRows({
    projectId,
    parentTaskId,
    isExpanded,
    depth,
    columnCount,
    onEditTask,
    onDeleteTask,
    onStatusChange,
    onPriorityChange,
    onTaskClick,
    projectMembers,
    onAssigneeChange,
    expandedTasks,
    setExpandedTasks,
    canEdit,
    onInlineCreate,
    onQuickCreate,
    customFields,
    onTitleChange,
    filterDefaults,
}: SubtaskRowsProps) {
    const { t } = useI18n();
    const { data, isLoading } = useSubtasks(projectId, parentTaskId, isExpanded);
    const [isAddingSubtask, setIsAddingSubtask] = useState(false);
    const [addingSubtaskToId, setAddingSubtaskToId] = useState<string | null>(null);
    const [newSubtaskTitle, setNewSubtaskTitle] = useState('');
    const [isCreating, setIsCreating] = useState(false);
    const [contextMenu, setContextMenu] = useState<{ x: number; y: number; task: Task } | null>(null);

    const handleCreateSubtask = async () => {
        if (!newSubtaskTitle.trim() || !onInlineCreate) return;
        setIsCreating(true);
        try {
            await onInlineCreate(newSubtaskTitle.trim(), parentTaskId);
            setNewSubtaskTitle('');
            setIsAddingSubtask(false);
        } catch (error) {
            console.error('Failed to create subtask:', error);
        } finally {
            setIsCreating(false);
        }
    };

    if (!isExpanded) return null;

    if (isLoading) {
        return (
            <tr className="border-t border-border bg-surface-hover/30">
                <td colSpan={columnCount} className="px-2 sm:px-3 md:px-4 py-2">
                    <div className="flex items-center gap-2" style={{ paddingLeft: `${depth * 24}px` }}>
                        <Loader2 className="h-4 w-4 animate-spin text-foreground-muted" />
                        <span className="text-sm text-foreground-muted">{t('tasks.loadingSubtasks')}</span>
                    </div>
                </td>
            </tr>
        );
    }

    const subtasks = data?.tasks || [];

    return (
        <>
            {subtasks.map((subtask) => {
                const isSubtaskExpanded = expandedTasks.has(subtask.id);
                const statusConf = statusConfig[subtask.status];
                const priorityConf = priorityConfig[subtask.priority];
                const StatusIcon = statusConf.icon;
                const PriorityIcon = priorityConf.icon;

                return (
                    <React.Fragment key={subtask.id}>
                        <tr
                            className="border-t border-border hover:bg-surface-hover/50 transition-colors bg-surface-hover/20 cursor-pointer"
                            onClick={(e) => {
                                // Don't trigger row click if clicking on interactive elements
                                const target = e.target as HTMLElement;
                                const isInteractive = target.closest('button, input, [role="checkbox"], [role="menuitem"], [data-radix-collection-item]');
                                if (!isInteractive && onTaskClick) {
                                    onTaskClick(subtask);
                                }
                            }}
                        >
                            {/* Checkbox placeholder */}
                            <td className="px-2 sm:px-3 md:px-4 py-2 sm:py-3 align-top w-10">
                                <div style={{ paddingLeft: `${depth * 16}px` }} />
                            </td>
                            {/* Title */}
                            <td className="px-2 sm:px-3 md:px-4 py-2 sm:py-3 align-top">
                                <SubtaskTitleCell
                                    subtask={subtask}
                                    depth={depth}
                                    isExpanded={isSubtaskExpanded}
                                    canEdit={canEdit}
                                    onToggleExpand={() => {
                                        setExpandedTasks(prev => {
                                            const newSet = new Set(prev);
                                            if (newSet.has(subtask.id)) {
                                                newSet.delete(subtask.id);
                                            } else {
                                                newSet.add(subtask.id);
                                            }
                                            return newSet;
                                        });
                                    }}
                                    onTitleChange={onTitleChange}
                                    onTaskClick={onTaskClick}
                                    onContextMenu={(e) => {
                                        if (!canEdit) return;
                                        e.preventDefault();
                                        setContextMenu({ x: e.clientX, y: e.clientY, task: subtask });
                                    }}
                                />
                            </td>
                            {/* Status */}
                            <td className="px-2 sm:px-3 md:px-4 py-2 sm:py-3 align-top">
                                <DropdownMenu>
                                    <DropdownMenuTrigger asChild disabled={!canEdit}>
                                        <Button variant="ghost" className="h-8 px-2">
                                            <Badge variant={statusConf.variant}>{getStatusLabel(subtask.status, t)}</Badge>
                                        </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="start">
                                        {Object.entries(statusConfig).map(([key, value]) => (
                                            <DropdownMenuItem
                                                key={key}
                                                onClick={() => onStatusChange(subtask.id, key as TaskStatus)}
                                            >
                                                <Badge variant={value.variant} className="mr-2">
                                                    {getStatusLabel(key as TaskStatus, t)}
                                                </Badge>
                                                {subtask.status === key && <span className="ml-auto">✓</span>}
                                            </DropdownMenuItem>
                                        ))}
                                    </DropdownMenuContent>
                                </DropdownMenu>
                            </td>
                            {/* Priority */}
                            <td className="px-2 sm:px-3 md:px-4 py-2 sm:py-3 align-top">
                                <DropdownMenu>
                                    <DropdownMenuTrigger asChild disabled={!canEdit}>
                                        <Button variant="ghost" className="h-8 px-2">
                                            <PriorityIcon className={cn('h-4 w-4 mr-1', priorityConf.color)} />
                                            <span className="text-sm">{getPriorityLabel(subtask.priority, t)}</span>
                                        </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="start">
                                        {Object.entries(priorityConfig).map(([key, value]) => {
                                            const Icon = value.icon;
                                            return (
                                                <DropdownMenuItem
                                                    key={key}
                                                    onClick={() => onPriorityChange(subtask.id, key as TaskPriority)}
                                                >
                                                    <Icon className={cn('h-4 w-4 mr-2', value.color)} />
                                                    {getPriorityLabel(key as TaskPriority, t)}
                                                    {subtask.priority === key && <span className="ml-auto">✓</span>}
                                                </DropdownMenuItem>
                                            );
                                        })}
                                    </DropdownMenuContent>
                                </DropdownMenu>
                            </td>
                            {/* Assignees */}
                            <td className="px-2 sm:px-3 md:px-4 py-2 sm:py-3 align-top">
                                <div className="flex items-center gap-1">
                                    {subtask.assignees.slice(0, 3).map((assignee) => (
                                        <Avatar key={assignee.userId} className="h-6 w-6">
                                            <AvatarImage src={assignee.user.avatarUrl || undefined} />
                                            <AvatarFallback className="text-xs">
                                                {getInitials(assignee.user.name)}
                                            </AvatarFallback>
                                        </Avatar>
                                    ))}
                                    {subtask.assignees.length > 3 && (
                                        <span className="text-xs text-foreground-muted">
                                            +{subtask.assignees.length - 3}
                                        </span>
                                    )}
                                </div>
                            </td>
                            {/* Due Date */}
                            <td className="px-2 sm:px-3 md:px-4 py-2 sm:py-3 align-top">
                                {subtask.dueDate && (
                                    <span className={cn(
                                        "text-sm",
                                        isOverdue(subtask.dueDate) && subtask.status !== 'DONE' && subtask.status !== 'CANCELLED'
                                            ? "text-red-500"
                                            : "text-foreground-muted"
                                    )}>
                                        {formatDate(subtask.dueDate)}
                                    </span>
                                )}
                            </td>
                            {/* Actions */}
                            <td className="px-2 sm:px-3 md:px-4 py-2 sm:py-3 align-top">
                                <DropdownMenu>
                                    <DropdownMenuTrigger asChild disabled={!canEdit}>
                                        <Button variant="ghost" size="icon" className="h-8 w-8">
                                            <MoreHorizontal className="h-4 w-4" />
                                        </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end">
                                        <DropdownMenuItem onClick={() => onEditTask(subtask)}>
                                            <Pencil className="mr-2 h-4 w-4" />
                                            {t('common.edit')}
                                        </DropdownMenuItem>
                                        <DropdownMenuItem onClick={() => {
                                            setAddingSubtaskToId(subtask.id);
                                            // Expand the subtask to show the inline input
                                            setExpandedTasks(prev => new Set([...prev, subtask.id]));
                                        }}>
                                            <Plus className="mr-2 h-4 w-4" />
                                            {t('tasks.addSubtask')}
                                        </DropdownMenuItem>
                                        <DropdownMenuSeparator />
                                        <DropdownMenuItem
                                            className="text-destructive"
                                            onClick={() => onDeleteTask(subtask.id)}
                                        >
                                            <Trash2 className="mr-2 h-4 w-4" />
                                            {t('common.delete')}
                                        </DropdownMenuItem>
                                    </DropdownMenuContent>
                                </DropdownMenu>
                            </td>
                        </tr>
                        {/* Inline add subtask row for this specific subtask */}
                        {addingSubtaskToId === subtask.id && (
                            <tr className="border-t border-border bg-surface-hover/20">
                                <td colSpan={columnCount} className="px-2 sm:px-3 md:px-4 py-2">
                                    <div style={{ paddingLeft: `${(depth + 1) * 16 + 24}px` }}>
                                        {onQuickCreate ? (
                                            <QuickAddTaskPopover
                                                onSubmit={async (data) => {
                                                    await onQuickCreate({
                                                        ...data,
                                                        parentId: subtask.id,
                                                        customFieldValues: data.customFieldValues,
                                                    });
                                                    setAddingSubtaskToId(null);
                                                }}
                                                projectMembers={projectMembers}
                                                parentId={subtask.id}
                                                isSubtask
                                                defaultExpanded
                                                onCancel={() => setAddingSubtaskToId(null)}
                                                customFields={customFields}
                                                filterDefaults={filterDefaults}
                                            />
                                        ) : (
                                            <div className="flex items-center gap-2">
                                                <Input
                                                    value={newSubtaskTitle}
                                                    onChange={(e) => setNewSubtaskTitle(e.target.value)}
                                                    onKeyDown={async (e) => {
                                                        if (e.key === 'Enter' && !e.shiftKey) {
                                                            e.preventDefault();
                                                            if (newSubtaskTitle.trim() && onInlineCreate) {
                                                                await onInlineCreate(newSubtaskTitle.trim(), subtask.id);
                                                                setNewSubtaskTitle('');
                                                                setAddingSubtaskToId(null);
                                                            }
                                                        } else if (e.key === 'Escape') {
                                                            setAddingSubtaskToId(null);
                                                            setNewSubtaskTitle('');
                                                        }
                                                    }}
                                                    placeholder={t('tasks.enterSubtaskTitle')}
                                                    className="flex-1 h-8"
                                                    autoFocus
                                                />
                                                <Button
                                                    size="sm"
                                                    onClick={async () => {
                                                        if (newSubtaskTitle.trim() && onInlineCreate) {
                                                            await onInlineCreate(newSubtaskTitle.trim(), subtask.id);
                                                            setNewSubtaskTitle('');
                                                            setAddingSubtaskToId(null);
                                                        }
                                                    }}
                                                    disabled={!newSubtaskTitle.trim()}
                                                >
                                                    {t('common.add')}
                                                </Button>
                                                <Button
                                                    size="sm"
                                                    variant="ghost"
                                                    onClick={() => {
                                                        setAddingSubtaskToId(null);
                                                        setNewSubtaskTitle('');
                                                    }}
                                                >
                                                    {t('common.cancel')}
                                                </Button>
                                            </div>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        )}
                        {/* Nested subtasks */}
                        {subtask.subtaskCount > 0 && (
                            <SubtaskRows
                                projectId={projectId}
                                parentTaskId={subtask.id}
                                isExpanded={isSubtaskExpanded}
                                depth={depth + 1}
                                columnCount={columnCount}
                                onEditTask={onEditTask}
                                onDeleteTask={onDeleteTask}
                                onStatusChange={onStatusChange}
                                onPriorityChange={onPriorityChange}
                                onTaskClick={onTaskClick}
                                projectMembers={projectMembers}
                                onAssigneeChange={onAssigneeChange}
                                expandedTasks={expandedTasks}
                                setExpandedTasks={setExpandedTasks}
                                canEdit={canEdit}
                                onInlineCreate={onInlineCreate}
                                onQuickCreate={onQuickCreate}
                                customFields={customFields}
                                onTitleChange={onTitleChange}
                                filterDefaults={filterDefaults}
                            />
                        )}
                    </React.Fragment>
                );
            })}
            {/* Add subtask row */}
            {canEdit && (onQuickCreate || onInlineCreate) && (
                <tr className="border-t border-border bg-surface-hover/20">
                    <td colSpan={columnCount} className="px-2 sm:px-3 md:px-4 py-2">
                        <div style={{ paddingLeft: `${depth * 16 + 24}px` }}>
                            {isAddingSubtask ? (
                                onQuickCreate ? (
                                    <QuickAddTaskPopover
                                        onSubmit={async (data) => {
                                            await onQuickCreate({
                                                ...data,
                                                parentId: parentTaskId,
                                                customFieldValues: data.customFieldValues,
                                            });
                                            setIsAddingSubtask(false);
                                        }}
                                        projectMembers={projectMembers}
                                        parentId={parentTaskId}
                                        isSubtask
                                        defaultExpanded
                                        onCancel={() => setIsAddingSubtask(false)}
                                        customFields={customFields}
                                        filterDefaults={filterDefaults}
                                    />
                                ) : (
                                    <div className="flex items-center gap-2">
                                        <Input
                                            value={newSubtaskTitle}
                                            onChange={(e) => setNewSubtaskTitle(e.target.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter' && !e.shiftKey) {
                                                    e.preventDefault();
                                                    handleCreateSubtask();
                                                } else if (e.key === 'Escape') {
                                                    setIsAddingSubtask(false);
                                                    setNewSubtaskTitle('');
                                                }
                                            }}
                                            placeholder={t('tasks.enterSubtaskTitle')}
                                            className="flex-1 h-8"
                                            autoFocus
                                            disabled={isCreating}
                                        />
                                        <Button
                                            size="sm"
                                            onClick={handleCreateSubtask}
                                            disabled={!newSubtaskTitle.trim() || isCreating}
                                        >
                                            {isCreating ? (
                                                <Loader2 className="h-4 w-4 animate-spin" />
                                            ) : (
                                                t('common.add')
                                            )}
                                        </Button>
                                        <Button
                                            size="sm"
                                            variant="ghost"
                                            onClick={() => {
                                                setIsAddingSubtask(false);
                                                setNewSubtaskTitle('');
                                            }}
                                            disabled={isCreating}
                                        >
                                            {t('common.cancel')}
                                        </Button>
                                    </div>
                                )
                            ) : (
                                <button
                                    onClick={() => setIsAddingSubtask(true)}
                                    className="flex items-center gap-2 text-foreground-muted hover:text-foreground transition-colors text-sm"
                                >
                                    <Plus className="h-4 w-4" />
                                    {t('tasks.addSubtask')}
                                </button>
                            )}
                        </div>
                    </td>
                </tr>
            )}

            {/* Right-click context menu for subtasks */}
            {contextMenu && (
                <DropdownMenu open={!!contextMenu} onOpenChange={(open) => !open && setContextMenu(null)}>
                    <DropdownMenuTrigger asChild disabled={!canEdit}>
                        <div style={{ position: 'fixed', left: contextMenu.x, top: contextMenu.y, width: 0, height: 0 }} />
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start">
                        <DropdownMenuItem onClick={() => { onEditTask(contextMenu.task); setContextMenu(null); }}>
                            <Pencil className="h-4 w-4 mr-2" />
                            {t('tasks.editTask')}
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => {
                            setAddingSubtaskToId(contextMenu.task.id);
                            setExpandedTasks(prev => new Set([...prev, contextMenu.task.id]));
                            setContextMenu(null);
                        }}>
                            <Plus className="h-4 w-4 mr-2" />
                            {t('tasks.addSubtask')}
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                            className="text-destructive"
                            onClick={() => { onDeleteTask(contextMenu.task.id); setContextMenu(null); }}
                        >
                            <Trash2 className="h-4 w-4 mr-2" />
                            {t('tasks.deleteTaskAction')}
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
            )}
        </>
    );
}

// ============================================
// CUSTOM FIELD HEADER COMPONENT
// ============================================

interface CustomFieldHeaderProps {
    field: CustomField;
    onEditCustomField?: (field: CustomField) => void;
    onDeleteCustomField?: (fieldId: string) => void;
}

function CustomFieldHeader({ field, onEditCustomField, onDeleteCustomField }: CustomFieldHeaderProps) {
    const { t } = useI18n();
    const [contextMenu, setContextMenu] = useState<{ x: number; y: number } | null>(null);
    const hasActions = onEditCustomField || onDeleteCustomField;

    return (
        <>
            <div
                className="flex items-center gap-2 cursor-move hover:text-foreground transition-colors py-1 select-none"
                onContextMenu={(e) => {
                    if (hasActions) {
                        e.preventDefault();
                        setContextMenu({ x: e.clientX, y: e.clientY });
                    }
                }}
            >
                <span className="truncate">{field.name}</span>
            </div>
            {contextMenu && hasActions && (
                <DropdownMenu open={!!contextMenu} onOpenChange={(open) => !open && setContextMenu(null)}>
                    <DropdownMenuTrigger asChild>
                        <div style={{ position: 'fixed', left: contextMenu.x, top: contextMenu.y, width: 1, height: 1, pointerEvents: 'none' }} />
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start" className="z-[100]" sideOffset={5}>
                        {onEditCustomField && (
                            <DropdownMenuItem onClick={() => { onEditCustomField(field); setContextMenu(null); }}>
                                <Pencil className="h-4 w-4 mr-2" />
                                {t('customFields.editField')}
                            </DropdownMenuItem>
                        )}
                        {onDeleteCustomField && (
                            <>
                                {onEditCustomField && <DropdownMenuSeparator />}
                                <DropdownMenuItem
                                    className="text-destructive"
                                    onClick={() => { onDeleteCustomField(field.id); setContextMenu(null); }}
                                >
                                    <Trash2 className="h-4 w-4 mr-2" />
                                    {t('customFields.deleteField')}
                                </DropdownMenuItem>
                            </>
                        )}
                    </DropdownMenuContent>
                </DropdownMenu>
            )}
        </>
    );
}

// ============================================
// PROPS
// ============================================

interface TaskTableProps {
    tasks: Task[];
    projectId: string;
    onEditTask: (task: Task) => void;
    onDeleteTask: (taskId: string) => void;
    onStatusChange: (taskId: string, status: TaskStatus) => void;
    onPriorityChange: (taskId: string, priority: TaskPriority) => void;
    onTaskClick?: (task: Task) => void;
    selectedTasks?: string[];
    onSelectionChange?: (taskIds: string[]) => void;
    isLoading?: boolean;
    onInlineCreate?: (title: string, parentId?: string) => Promise<void>;
    onQuickCreate?: (data: {
        title: string;
        status: TaskStatus;
        priority: TaskPriority;
        dueDate: string | null;
        assigneeIds: string[];
        parentId?: string;
        customFieldValues?: Record<string, unknown>;
    }) => Promise<void>;
    onTitleChange?: (taskId: string, title: string) => Promise<void>;
    canEdit?: boolean;
    columnOrder?: string[];
    hiddenColumns?: string[];
    onColumnOrderChange?: (columnOrder: string[]) => void;
    onHiddenColumnsChange?: (hiddenColumns: string[]) => void;
    customFields?: CustomField[];
    projectMembers?: Array<{ userId: string; user: { id: string; name: string; avatarUrl: string | null } }>;
    onAssigneeChange?: (taskId: string, assigneeIds: string[]) => void;
    onDueDateChange?: (taskId: string, dueDate: string | null) => void;
    onCustomFieldChange?: (taskId: string, fieldId: string, value: unknown) => void;
    onEditCustomField?: (field: CustomField) => void;
    onDeleteCustomField?: (fieldId: string) => void;
    // Bulk actions
    onBulkDelete?: (taskIds: string[]) => Promise<void>;
    onBulkStatusChange?: (taskIds: string[], status: TaskStatus) => Promise<void>;
    onBulkPriorityChange?: (taskIds: string[], priority: TaskPriority) => Promise<void>;
    onBulkAssigneeChange?: (taskIds: string[], assigneeIds: string[]) => Promise<void>;
    onBulkCustomFieldChange?: (taskIds: string[], fieldId: string, value: unknown) => Promise<void>;
    filterDefaults?: FilterDefaults;
}

// ============================================
// COMPONENT
// ============================================

export function TaskTable({
    tasks,
    projectId,
    onEditTask,
    onDeleteTask,
    onStatusChange,
    onPriorityChange,
    onTaskClick,
    selectedTasks: _selectedTasks = [],
    onSelectionChange,
    isLoading,
    onInlineCreate,
    onQuickCreate,
    onTitleChange,
    canEdit = true,
    columnOrder,
    hiddenColumns,
    onColumnOrderChange,
    onHiddenColumnsChange,
    customFields = [],
    projectMembers = [],
    onAssigneeChange,
    onDueDateChange,
    onCustomFieldChange,
    onEditCustomField,
    onDeleteCustomField,
    onBulkDelete,
    onBulkStatusChange,
    onBulkPriorityChange,
    onBulkAssigneeChange,
    onBulkCustomFieldChange,
    filterDefaults,
}: TaskTableProps) {
    const { t } = useI18n();
    const [sorting, setSorting] = useState<SortingState>([]);
    const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
    const [expandedTasks, setExpandedTasks] = useState<Set<string>>(new Set());
    const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
    const [internalColumnOrder, setInternalColumnOrder] = useState<ColumnOrderState>([]);
    const [rowSelection, setRowSelection] = useState<Record<string, boolean>>({});
    const [isAddingTask, setIsAddingTask] = useState(false);
    const [newTaskTitle, setNewTaskTitle] = useState('');
    const [isCreating, setIsCreating] = useState(false);
    const [draggedColumn, setDraggedColumn] = useState<string | null>(null);
    const [showColumnMenu, setShowColumnMenu] = useState(false);
    const [contextMenuField, setContextMenuField] = useState<CustomField | null>(null);
    const [addingSubtaskTo, setAddingSubtaskTo] = useState<string | null>(null);
    const [openMultiSelectCell, setOpenMultiSelectCell] = useState<string | null>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const tableScrollbarRef = useRef<HTMLDivElement>(null);
    // R5 fix: sticky horizontal scrollbar INSIDE the table at the
    // top. The bar is a sibling of the <table> inside the table's
    // scroll container, with `position: sticky; top: 0;` so when
    // the user scrolls the page vertically through many task rows,
    // the bar sticks to the viewport top and remains draggable.
    //
    // This REPLACES the R3/R4 viewport-fixed bar (`fixed top-0`)
    // — that bar was a global overlay, not part of the table DOM,
    // and the user explicitly wanted the bar to be a sticky
    // element INSIDE the table.
    const stickyScrollbarRef = useRef<HTMLDivElement>(null);
    const [showStickyTop, setShowStickyTop] = useState(false);

    // 2-way sync between the sticky bar and the table. R5 dropped
    // the 3-way sync from R3/R4 since there's no longer a
    // separate viewport-pinned element to coordinate with.
    const syncHorizontalScroll = useCallback((source: 'sticky' | 'table') => {
        const stickyEl = stickyScrollbarRef.current;
        const tableEl = tableScrollbarRef.current;
        if (source === 'sticky' && stickyEl && tableEl) {
            if (Math.abs(tableEl.scrollLeft - stickyEl.scrollLeft) > 1) {
                tableEl.scrollLeft = stickyEl.scrollLeft;
            }
        } else if (source === 'table' && stickyEl && tableEl) {
            if (Math.abs(stickyEl.scrollLeft - tableEl.scrollLeft) > 1) {
                stickyEl.scrollLeft = tableEl.scrollLeft;
            }
        }
    }, []);

    // Default column IDs (order matters) - include custom field columns
    const customFieldColumnIds = customFields.map(f => `custom_${f.id}`);
    const defaultColumnIds = ['select', 'title', 'status', 'priority', 'assignees', 'dueDate', ...customFieldColumnIds, 'actions'];

    // Sync external columnOrder with internal state
    useEffect(() => {
        if (columnOrder && columnOrder.length > 0) {
            // Merge with any new custom field columns
            const existingOrder = [...columnOrder];
            customFieldColumnIds.forEach(cfId => {
                if (!existingOrder.includes(cfId)) {
                    existingOrder.splice(existingOrder.length - 1, 0, cfId); // Insert before 'actions'
                }
            });
            setInternalColumnOrder(existingOrder);
        } else {
            setInternalColumnOrder(defaultColumnIds);
        }
    }, [columnOrder, customFields]);

    // Sync external hiddenColumns with internal visibility state
    useEffect(() => {
        if (hiddenColumns) {
            const visibility: VisibilityState = {};
            hiddenColumns.forEach(col => {
                visibility[col] = false;
            });
            setColumnVisibility(visibility);
        }
    }, [hiddenColumns]);

    const handleInlineCreate = useCallback(async () => {
        if (!newTaskTitle.trim() || !onInlineCreate) return;

        setIsCreating(true);
        try {
            await onInlineCreate(newTaskTitle.trim());
            setNewTaskTitle('');
            setIsAddingTask(false);
        } catch (error) {
            console.error('Failed to create task:', error);
        } finally {
            setIsCreating(false);
        }
    }, [newTaskTitle, onInlineCreate]);

    const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleInlineCreate();
        } else if (e.key === 'Escape') {
            setIsAddingTask(false);
            setNewTaskTitle('');
        }
    }, [handleInlineCreate]);

    // Column drag handlers
    const handleDragStart = useCallback((e: React.DragEvent, columnId: string) => {
        setDraggedColumn(columnId);
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', columnId);
    }, []);

    const handleDragOver = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
    }, []);

    const handleDrop = useCallback((e: React.DragEvent, targetColumnId: string) => {
        e.preventDefault();
        if (!draggedColumn || draggedColumn === targetColumnId) {
            setDraggedColumn(null);
            return;
        }

        // Don't allow reordering select or actions columns
        if (draggedColumn === 'select' || draggedColumn === 'actions' ||
            targetColumnId === 'select' || targetColumnId === 'actions') {
            setDraggedColumn(null);
            return;
        }

        const currentOrder = internalColumnOrder.length > 0 ? [...internalColumnOrder] : [...defaultColumnIds];
        const fromIndex = currentOrder.indexOf(draggedColumn);
        const toIndex = currentOrder.indexOf(targetColumnId);

        if (fromIndex !== -1 && toIndex !== -1) {
            currentOrder.splice(fromIndex, 1);
            currentOrder.splice(toIndex, 0, draggedColumn);
            setInternalColumnOrder(currentOrder);
            onColumnOrderChange?.(currentOrder);
        }

        setDraggedColumn(null);
    }, [draggedColumn, internalColumnOrder, onColumnOrderChange, defaultColumnIds]);

    const handleDragEnd = useCallback(() => {
        setDraggedColumn(null);
    }, []);

    // Toggle column visibility
    const toggleColumnVisibility = useCallback((columnId: string) => {
        const newVisibility = { ...columnVisibility };
        const isCurrentlyVisible = columnVisibility[columnId] !== false;
        newVisibility[columnId] = !isCurrentlyVisible;
        setColumnVisibility(newVisibility);

        // Notify parent of hidden columns change
        const newHiddenColumns = Object.keys(newVisibility).filter(key => newVisibility[key] === false);
        onHiddenColumnsChange?.(newHiddenColumns);
    }, [columnVisibility, onHiddenColumnsChange]);

    // Get column display name
    const getColumnName = useCallback((columnId: string) => {
        // Check if it's a custom field column
        if (columnId.startsWith('custom_')) {
            const fieldId = columnId.replace('custom_', '');
            const field = customFields.find(f => f.id === fieldId);
            return field?.name || columnId;
        }
        const names: Record<string, string> = {
            select: t('table.selection'),
            title: t('table.task'),
            status: t('tasks.status'),
            priority: t('tasks.priority'),
            assignees: t('tasks.assignees'),
            dueDate: t('tasks.dueDate'),
            actions: t('table.actions'),
        };
        return names[columnId] || columnId;
    }, [customFields, t]);

    // Build custom field columns
    const customFieldColumns = useMemo<ColumnDef<Task>[]>(() => {
        return customFields.map(field => ({
            id: `custom_${field.id}`,
            accessorKey: `customFieldValues.${field.id}`,
            header: () => (
                <CustomFieldHeader
                    field={field}
                    onEditCustomField={onEditCustomField}
                    onDeleteCustomField={onDeleteCustomField}
                />
            ),
            cell: ({ row }) => {
                const task = row.original;
                const value = task.customFieldValues?.[field.id];

                // For MULTI_SELECT fields
                if (field.type === 'MULTI_SELECT') {
                    const selectedIds = Array.isArray(value) ? value : [];
                    const selectedOptions = field.options.filter(opt => selectedIds.includes(opt.id));
                    const popoverId = `${task.id}:${field.id}`;

                    return (
                        <Popover
                            open={openMultiSelectCell === popoverId}
                            onOpenChange={(open) => setOpenMultiSelectCell(open ? popoverId : null)}
                        >
                            <PopoverTrigger asChild>
                                <button className="flex flex-wrap gap-1 min-h-[28px] max-w-[200px] items-center cursor-pointer hover:bg-surface-hover rounded px-1 py-0.5">
                                    {selectedOptions.length > 0 ? (
                                        selectedOptions.map(opt => (
                                            <Badge
                                                key={opt.id}
                                                variant="secondary"
                                                className="text-xs"
                                                style={opt.color ? {
                                                    backgroundColor: `${opt.color}20`,
                                                    borderColor: opt.color,
                                                    color: opt.color
                                                } : undefined}
                                            >
                                                {opt.label}
                                            </Badge>
                                        ))
                                    ) : (
                                        <span className="text-foreground-muted text-sm">—</span>
                                    )}
                                </button>
                            </PopoverTrigger>
                            <PopoverContent className="w-56 p-2" align="start" onOpenAutoFocus={(e) => e.preventDefault()}>
                                <div className="space-y-1 max-h-48 overflow-y-auto">
                                    {field.options.map(opt => (
                                        <div
                                            key={opt.id}
                                            className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-surface-hover cursor-pointer"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                if (!onCustomFieldChange) return;
                                                const newValue = selectedIds.includes(opt.id)
                                                    ? selectedIds.filter(id => id !== opt.id)
                                                    : [...selectedIds, opt.id];
                                                onCustomFieldChange(task.id, field.id, newValue);
                                            }}
                                        >
                                            <Checkbox
                                                checked={selectedIds.includes(opt.id)}
                                                onCheckedChange={() => {
                                                    // Handled by parent div click
                                                }}
                                            />
                                            <span
                                                className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium text-white"
                                                style={{ backgroundColor: opt.color || '#6366f1' }}
                                            >
                                                {opt.label}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </PopoverContent>
                        </Popover>
                    );
                }

                // For SELECT fields
                if (field.type === 'SELECT') {
                    const selectedOption = field.options.find(opt => opt.id === value);
                    return (
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild disabled={!canEdit}>
                                <button className="flex items-center gap-1 cursor-pointer hover:bg-surface-hover rounded px-1 py-0.5">
                                    {selectedOption ? (
                                        <Badge
                                            variant="secondary"
                                            className="text-xs"
                                            style={selectedOption.color ? {
                                                backgroundColor: `${selectedOption.color}20`,
                                                borderColor: selectedOption.color,
                                                color: selectedOption.color
                                            } : undefined}
                                        >
                                            {selectedOption.label}
                                        </Badge>
                                    ) : (
                                        <span className="text-foreground-muted text-sm">—</span>
                                    )}
                                </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="start">
                                <DropdownMenuItem onClick={() => onCustomFieldChange?.(task.id, field.id, null)}>
                                    <span className="text-foreground-muted">None</span>
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                {field.options.map(opt => (
                                    <DropdownMenuItem
                                        key={opt.id}
                                        onClick={() => onCustomFieldChange?.(task.id, field.id, opt.id)}
                                    >
                                        <span
                                            className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium text-white"
                                            style={{ backgroundColor: opt.color || '#6366f1' }}
                                        >
                                            {opt.label}
                                        </span>
                                        {value === opt.id && <span className="ml-auto">✓</span>}
                                    </DropdownMenuItem>
                                ))}
                            </DropdownMenuContent>
                        </DropdownMenu>
                    );
                }

                // For SPRINT fields — R2 fix: SPRINT used to fall through
                // to the "unknown field type" plain-span fallback, which
                // meant clicking the cell fired the row's onClick and
                // opened the side panel instead of a picker. Now mirrors
                // the SELECT pattern with a DropdownMenu picker that lists
                // each sprint (colour dot + name + date range).
                if (field.type === 'SPRINT') {
                    const sprints = parseSprintOptionsForField(field.options);
                    const selectedSprintId = typeof value === 'string' ? value : null;
                    const activeSprint = sprints.find((s) => s.id === selectedSprintId);

                    return (
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild disabled={!canEdit}>
                                <button
                                    className="flex items-center gap-2 cursor-pointer hover:bg-surface-hover rounded px-1 py-0.5 max-w-[200px]"
                                    onClick={(e) => {
                                        // Stop propagation so the row's
                                        // onClick handler doesn't fire and
                                        // open the side panel behind the
                                        // dropdown. (Radix role='menuitem'
                                        // already covers the menu items,
                                        // this covers the trigger button.)
                                        e.stopPropagation();
                                    }}
                                >
                                    {activeSprint ? (
                                        <>
                                            <span
                                                className="inline-block w-2 h-2 rounded-full shrink-0"
                                                style={{ backgroundColor: activeSprint.color || '#6366f1' }}
                                            />
                                            <span className="text-sm truncate">{activeSprint.name}</span>
                                        </>
                                    ) : (
                                        <span className="text-foreground-muted text-sm">—</span>
                                    )}
                                </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="start">
                                <DropdownMenuItem
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onCustomFieldChange?.(task.id, field.id, null);
                                    }}
                                >
                                    <span className="text-foreground-muted">None</span>
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                {sprints.length === 0 ? (
                                    <DropdownMenuItem disabled>
                                        <span className="text-foreground-muted text-xs">
                                            No sprints configured
                                        </span>
                                    </DropdownMenuItem>
                                ) : (
                                    sprints.map((s) => (
                                        <DropdownMenuItem
                                            key={s.id}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onCustomFieldChange?.(task.id, field.id, s.id);
                                            }}
                                        >
                                            <span className="flex items-center gap-2 min-w-0">
                                                <span
                                                    className="inline-block w-2 h-2 rounded-full shrink-0"
                                                    style={{ backgroundColor: s.color || '#6366f1' }}
                                                />
                                                <span className="truncate">{s.name}</span>
                                                {s.startDate || s.endDate ? (
                                                    <span className="text-foreground-muted text-xs shrink-0">
                                                        ({s.startDate || '?'} → {s.endDate || '?'})
                                                    </span>
                                                ) : null}
                                            </span>
                                            {selectedSprintId === s.id && <span className="ml-auto">✓</span>}
                                        </DropdownMenuItem>
                                    ))
                                )}
                            </DropdownMenuContent>
                        </DropdownMenu>
                    );
                }

                // For CHECKBOX fields
                if (field.type === 'CHECKBOX') {
                    return (
                        <div className="flex items-center">
                            <Checkbox
                                checked={value === true || value === 'true'}
                                onCheckedChange={(checked) => {
                                    onCustomFieldChange?.(task.id, field.id, checked);
                                }}
                            />
                        </div>
                    );
                }

                // For TEXT, NUMBER, URL, EMAIL fields - inline editing
                if (field.type === 'TEXT' || field.type === 'NUMBER' || field.type === 'URL' || field.type === 'EMAIL') {
                    const [isEditing, setIsEditing] = useState(false);
                    const [editValue, setEditValue] = useState(value?.toString() || '');

                    const handleSave = () => {
                        let finalValue: any = editValue;
                        if (field.type === 'NUMBER') {
                            finalValue = editValue ? parseFloat(editValue) : null;
                        }
                        onCustomFieldChange?.(task.id, field.id, finalValue || null);
                        setIsEditing(false);
                    };

                    if (isEditing) {
                        return (
                            <Input
                                type={field.type === 'NUMBER' ? 'number' : field.type === 'EMAIL' ? 'email' : field.type === 'URL' ? 'url' : 'text'}
                                value={editValue}
                                onChange={(e) => setEditValue(e.target.value)}
                                onBlur={handleSave}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') handleSave();
                                    if (e.key === 'Escape') {
                                        setEditValue(value?.toString() || '');
                                        setIsEditing(false);
                                    }
                                }}
                                className="h-7 text-sm"
                                autoFocus
                            />
                        );
                    }

                    return (
                        <button
                            onClick={() => {
                                setEditValue(value?.toString() || '');
                                setIsEditing(true);
                            }}
                            className="text-sm text-foreground-secondary hover:bg-surface-hover rounded px-2 py-1 w-full text-left"
                        >
                            {value !== null && value !== undefined ? String(value) : '—'}
                        </button>
                    );
                }

                // For DATE fields
                if (field.type === 'DATE') {
                    const dateValue = value ? new Date(value as string) : null;

                    return (
                        <Popover>
                            <PopoverTrigger asChild>
                                <button className="flex items-center gap-2 cursor-pointer hover:bg-surface-hover rounded px-2 py-1 text-sm">
                                    <Calendar className="h-4 w-4 text-foreground-muted" />
                                    <span className={cn(
                                        "text-foreground-secondary",
                                        !dateValue && "text-foreground-muted"
                                    )}>
                                        {dateValue ? format(dateValue, 'MMM d, yyyy') : '—'}
                                    </span>
                                </button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-3" align="start">
                                <Input
                                    type="date"
                                    value={dateValue ? format(dateValue, 'yyyy-MM-dd') : ''}
                                    onChange={(e) => {
                                        const newDate = e.target.value ? new Date(e.target.value).toISOString() : null;
                                        onCustomFieldChange?.(task.id, field.id, newDate);
                                    }}
                                    className="w-full"
                                />
                                {dateValue && (
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => onCustomFieldChange?.(task.id, field.id, null)}
                                        className="w-full mt-2"
                                    >
                                        Clear
                                    </Button>
                                )}
                            </PopoverContent>
                        </Popover>
                    );
                }

                // For PERSON fields - simple user selector
                if (field.type === 'PERSON') {
                    const selectedMemberId = value as string | null;
                    const selectedMember = projectMembers?.find(m => m.userId === selectedMemberId);

                    return (
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild disabled={!canEdit}>
                                <button
                                    className="flex items-center gap-2 cursor-pointer hover:bg-surface-hover rounded px-1 py-0.5"
                                    onClick={(e) => {
                                        // Stop propagation so the row's
                                        // onClick handler doesn't fire and
                                        // open the side panel behind the
                                        // dropdown.
                                        e.stopPropagation();
                                    }}
                                >
                                    {selectedMember ? (
                                        <>
                                            <Avatar className="h-6 w-6">
                                                <AvatarImage src={selectedMember.user.avatarUrl || undefined} />
                                                <AvatarFallback className="text-xs">
                                                    {getInitials(selectedMember.user.name)}
                                                </AvatarFallback>
                                            </Avatar>
                                            <span className="text-sm">{selectedMember.user.name}</span>
                                        </>
                                    ) : (
                                        <span className="text-foreground-muted text-sm">—</span>
                                    )}
                                </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="start">
                                {/* R2 fix: "None" used to silently no-op when
                                    onCustomFieldChange was undefined OR when
                                    the parent didn't refresh the cell. Now we
                                    explicitly pass null and stop propagation
                                    so the row's onClick can't intercept the
                                    click. The parent's handleCustomFieldChange
                                    already PATCHes `{ ...currentValues,
                                    [fieldId]: null }` and refetches. */}
                                <DropdownMenuItem
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                        if (!onCustomFieldChange) return;
                                        onCustomFieldChange(task.id, field.id, null);
                                    }}
                                >
                                    <span className="text-foreground-muted">None</span>
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                {projectMembers?.map(member => (
                                    <DropdownMenuItem
                                        key={member.userId}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            if (!onCustomFieldChange) return;
                                            onCustomFieldChange(task.id, field.id, member.userId);
                                        }}
                                    >
                                        <Avatar className="h-6 w-6 mr-2">
                                            <AvatarImage src={member.user.avatarUrl || undefined} />
                                            <AvatarFallback className="text-xs">
                                                {getInitials(member.user.name)}
                                            </AvatarFallback>
                                        </Avatar>
                                        {member.user.name}
                                        {selectedMemberId === member.userId && <span className="ml-auto">✓</span>}
                                    </DropdownMenuItem>
                                ))}
                            </DropdownMenuContent>
                        </DropdownMenu>
                    );
                }

                // Fallback for unknown field types
                return (
                    <span className="text-sm text-foreground-secondary">
                        {value !== null && value !== undefined ? String(value) : '—'}
                    </span>
                );
            },
            size: 120,
        }));
    }, [customFields, onCustomFieldChange, onEditCustomField, onDeleteCustomField, projectMembers, openMultiSelectCell]);

    // Define columns
    const columns = useMemo<ColumnDef<Task>[]>(
        () => [
            // Selection column
            {
                id: 'select',
                header: ({ table }) => (
                    <Checkbox
                        checked={
                            table.getIsAllPageRowsSelected() ||
                            (table.getIsSomePageRowsSelected() && 'indeterminate')
                        }
                        onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
                        aria-label="Select all"
                        className="translate-y-[2px]"
                    />
                ),
                cell: ({ row }) => (
                    <div onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                            checked={row.getIsSelected()}
                            onCheckedChange={(value) => {
                                row.toggleSelected(!!value);
                                // Update last selected index when directly clicking checkbox
                                const rowIndex = table.getRowModel().rows.findIndex(r => r.id === row.id);
                                setLastSelectedIndex(rowIndex);
                            }}
                            aria-label="Select row"
                            className="translate-y-[2px]"
                        />
                    </div>
                ),
                enableSorting: false,
                enableHiding: false,
                size: 40,
            },
            // Title column
            {
                accessorKey: 'title',
                header: ({ column }) => (
                    <Button
                        variant="ghost"
                        onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
                        className="-ml-4 h-8 hover:bg-transparent"
                    >
                        {t('table.task')}
                        {column.getIsSorted() === 'asc' ? (
                            <ChevronUp className="ml-2 h-4 w-4" />
                        ) : column.getIsSorted() === 'desc' ? (
                            <ChevronDown className="ml-2 h-4 w-4" />
                        ) : null}
                    </Button>
                ),
                cell: ({ row }) => {
                    const task = row.original;
                    const handleClick = onTaskClick || onEditTask;
                    const isExpanded = expandedTasks.has(task.id);
                    const [contextMenu, setContextMenu] = useState<{ x: number; y: number } | null>(null);
                    const [isEditing, setIsEditing] = useState(false);
                    const [editValue, setEditValue] = useState(task.title);
                    const [isSaving, setIsSaving] = useState(false);
                    const inputRef = useRef<HTMLInputElement>(null);
                    const longPressTimerRef = useRef<number | null>(null);
                    const touchStartRef = useRef<{ x: number; y: number } | null>(null);
                    const clickTimerRef = useRef<NodeJS.Timeout | null>(null);

                    // Handle title save
                    const handleSaveTitle = async () => {
                        if (editValue.trim() === task.title || !editValue.trim()) {
                            setEditValue(task.title);
                            setIsEditing(false);
                            return;
                        }
                        if (!onTitleChange) {
                            setIsEditing(false);
                            return;
                        }
                        setIsSaving(true);
                        try {
                            await onTitleChange(task.id, editValue.trim());
                            setIsEditing(false);
                        } catch (error) {
                            console.error('Failed to update title:', error);
                            setEditValue(task.title);
                            setIsEditing(false);
                        } finally {
                            setIsSaving(false);
                        }
                    };

                    // Handle touch start for long-press detection (mobile edit)
                    const handleTouchStart = (e: React.TouchEvent) => {
                        const touch = e.touches[0];
                        if (!touch) return;
                        touchStartRef.current = { x: touch.clientX, y: touch.clientY };
                        longPressTimerRef.current = window.setTimeout(() => {
                            // Long press triggers edit mode
                            e.preventDefault();
                            setEditValue(task.title);
                            setIsEditing(true);
                        }, 500); // 500ms for long press
                    };

                    // Handle touch move (cancel long press if moved too much)
                    const handleTouchMove = (e: React.TouchEvent) => {
                        if (touchStartRef.current && longPressTimerRef.current) {
                            const touch = e.touches[0];
                            if (!touch) return;
                            const dx = Math.abs(touch.clientX - touchStartRef.current.x);
                            const dy = Math.abs(touch.clientY - touchStartRef.current.y);
                            if (dx > 10 || dy > 10) {
                                clearTimeout(longPressTimerRef.current);
                                longPressTimerRef.current = null;
                            }
                        }
                    };

                    // Handle touch end (clear timer)
                    const handleTouchEnd = () => {
                        if (longPressTimerRef.current) {
                            clearTimeout(longPressTimerRef.current);
                            longPressTimerRef.current = null;
                        }
                        touchStartRef.current = null;
                    };

                    // Focus input when editing starts
                    useEffect(() => {
                        if (isEditing && inputRef.current) {
                            inputRef.current.focus();
                            inputRef.current.select();
                        }
                    }, [isEditing]);

                    return (
                        <>
                            <div className="flex items-start gap-2">
                                {task.subtaskCount > 0 && (
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setExpandedTasks(prev => {
                                                const newSet = new Set(prev);
                                                if (newSet.has(task.id)) {
                                                    newSet.delete(task.id);
                                                } else {
                                                    newSet.add(task.id);
                                                }
                                                return newSet;
                                            });
                                        }}
                                        className="p-1 hover:bg-surface-hover rounded transition-transform"
                                    >
                                        {isExpanded ? (
                                            <ChevronDown className="h-4 w-4 text-foreground-muted" />
                                        ) : (
                                            <ChevronRight className="h-4 w-4 text-foreground-muted" />
                                        )}
                                    </button>
                                )}
                                {task.subtaskCount === 0 && (
                                    <div className="w-6" /> // Spacer for alignment
                                )}
                                <div className="flex-1 min-w-0">
                                    {isEditing ? (
                                        <div className="flex items-center gap-1">
                                            <Input
                                                ref={inputRef}
                                                value={editValue}
                                                onChange={(e) => setEditValue(e.target.value)}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter') {
                                                        e.preventDefault();
                                                        handleSaveTitle();
                                                    } else if (e.key === 'Escape') {
                                                        setEditValue(task.title);
                                                        setIsEditing(false);
                                                    }
                                                }}
                                                onBlur={handleSaveTitle}
                                                className="h-7 text-sm font-medium"
                                                disabled={isSaving}
                                            />
                                            <Button
                                                size="icon"
                                                variant="ghost"
                                                className="h-7 w-7 shrink-0"
                                                onClick={handleSaveTitle}
                                                disabled={isSaving}
                                            >
                                                {isSaving ? (
                                                    <Loader2 className="h-3 w-3 animate-spin" />
                                                ) : (
                                                    <Check className="h-3 w-3 text-green-500" />
                                                )}
                                            </Button>
                                            <Button
                                                size="icon"
                                                variant="ghost"
                                                className="h-7 w-7 shrink-0"
                                                onClick={() => {
                                                    setEditValue(task.title);
                                                    setIsEditing(false);
                                                }}
                                                disabled={isSaving}
                                            >
                                                <X className="h-3 w-3 text-red-500" />
                                            </Button>
                                        </div>
                                    ) : (
                                        <button
                                            onClick={(e) => {
                                                // Use a timer to distinguish between single and double click
                                                if (clickTimerRef.current) {
                                                    clearTimeout(clickTimerRef.current);
                                                }
                                                clickTimerRef.current = setTimeout(() => {
                                                    // Single click: open task detail panel
                                                    if (onTaskClick) {
                                                        onTaskClick(task);
                                                    }
                                                }, 200);
                                            }}
                                            onDoubleClick={(e) => {
                                                // Clear the single click timer
                                                if (clickTimerRef.current) {
                                                    clearTimeout(clickTimerRef.current);
                                                    clickTimerRef.current = null;
                                                }
                                                e.preventDefault();
                                                e.stopPropagation();
                                                // Double click: enable editing mode
                                                if (onTitleChange && canEdit) {
                                                    setEditValue(task.title);
                                                    setIsEditing(true);
                                                }
                                            }}
                                            onTouchStart={onTitleChange && canEdit ? handleTouchStart : undefined}
                                            onTouchMove={onTitleChange && canEdit ? handleTouchMove : undefined}
                                            onTouchEnd={onTitleChange && canEdit ? handleTouchEnd : undefined}
                                            onContextMenu={(e) => {
                                        if (!canEdit) return;
                                                e.preventDefault();
                                                setContextMenu({ x: e.clientX, y: e.clientY });
                                            }}
                                            className="text-left font-medium text-foreground hover:text-primary block w-full whitespace-normal break-words touch-manipulation"
                                            title={task.title}
                                        >
                                            {task.title}
                                        </button>
                                    )}
                                    {task.description && !isEditing && (
                                        <p className="text-xs text-foreground-muted truncate">
                                            {task.description}
                                        </p>
                                    )}
                                </div>
                                {/* Indicators */}
                                {!isEditing && (
                                    <div className="flex items-center gap-1 text-foreground-muted">
                                        {task.subtaskCount > 0 && (
                                            <span className="text-xs flex items-center gap-0.5">
                                                <ChevronRight className="h-3 w-3" />
                                                {task.subtaskCount}
                                            </span>
                                        )}
                                        {task.commentCount > 0 && (
                                            <span className="text-xs flex items-center gap-0.5">
                                                <MessageSquare className="h-3 w-3" />
                                                {task.commentCount}
                                            </span>
                                        )}
                                        {task.attachmentCount > 0 && (
                                            <span className="text-xs flex items-center gap-0.5">
                                                <Paperclip className="h-3 w-3" />
                                                {task.attachmentCount}
                                            </span>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Right-click context menu */}
                            {contextMenu && (
                                <DropdownMenu open={!!contextMenu} onOpenChange={(open) => !open && setContextMenu(null)}>
                                    <DropdownMenuTrigger asChild disabled={!canEdit}>
                                        <div style={{ position: 'fixed', left: contextMenu.x, top: contextMenu.y, width: 0, height: 0 }} />
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="start">
                                        <DropdownMenuItem onClick={() => { onEditTask(task); setContextMenu(null); }}>
                                            <Pencil className="h-4 w-4 mr-2" />
                                            {t('tasks.editTask')}
                                        </DropdownMenuItem>
                                        {onTitleChange && canEdit && (
                                            <DropdownMenuItem onClick={() => {
                                                setEditValue(task.title);
                                                setIsEditing(true);
                                                setContextMenu(null);
                                            }}>
                                                <Pencil className="h-4 w-4 mr-2" />
                                                {t('tasks.editTitle')}
                                            </DropdownMenuItem>
                                        )}
                                        {(onQuickCreate || onInlineCreate) && canEdit && (
                                            <DropdownMenuItem onClick={() => {
                                                setAddingSubtaskTo(task.id);
                                                setContextMenu(null);
                                            }}>
                                                <Plus className="h-4 w-4 mr-2" />
                                                {t('tasks.addSubtask')}
                                            </DropdownMenuItem>
                                        )}
                                        <DropdownMenuSeparator />
                                        <DropdownMenuItem
                                            className="text-destructive"
                                            onClick={() => { onDeleteTask(task.id); setContextMenu(null); }}
                                        >
                                            <Trash2 className="h-4 w-4 mr-2" />
                                            {t('tasks.deleteTaskAction')}
                                        </DropdownMenuItem>
                                    </DropdownMenuContent>
                                </DropdownMenu>
                            )}
                        </>
                    );
                },
                size: 320,
                minSize: 260,
                maxSize: 360,
            },
            // Status column
            {
                accessorKey: 'status',
                header: ({ column }) => (
                    <Button
                        variant="ghost"
                        onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
                        className="-ml-4 h-8 hover:bg-transparent"
                    >
                        {t('tasks.status')}
                        {column.getIsSorted() === 'asc' ? (
                            <ChevronUp className="ml-2 h-4 w-4" />
                        ) : column.getIsSorted() === 'desc' ? (
                            <ChevronDown className="ml-2 h-4 w-4" />
                        ) : null}
                    </Button>
                ),
                cell: ({ row }) => {
                    const status = row.original.status;
                    const config = statusConfig[status];
                    return (
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild disabled={!canEdit}>
                                <Button variant="ghost" className="h-8 px-2">
                                    <Badge variant={config.variant}>{getStatusLabel(status, t)}</Badge>
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="start">
                                {Object.entries(statusConfig).map(([key, value]) => (
                                    <DropdownMenuItem
                                        key={key}
                                        onClick={() => onStatusChange(row.original.id, key as TaskStatus)}
                                    >
                                        <Badge variant={value.variant} className="mr-2">
                                            {getStatusLabel(key as TaskStatus, t)}
                                        </Badge>
                                        {status === key && <span className="ml-auto">✓</span>}
                                    </DropdownMenuItem>
                                ))}
                            </DropdownMenuContent>
                        </DropdownMenu>
                    );
                },
                size: 130,
            },
            // Priority column
            {
                accessorKey: 'priority',
                header: ({ column }) => (
                    <Button
                        variant="ghost"
                        onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
                        className="-ml-4 h-8 hover:bg-transparent"
                    >
                        {t('tasks.priority')}
                        {column.getIsSorted() === 'asc' ? (
                            <ChevronUp className="ml-2 h-4 w-4" />
                        ) : column.getIsSorted() === 'desc' ? (
                            <ChevronDown className="ml-2 h-4 w-4" />
                        ) : null}
                    </Button>
                ),
                cell: ({ row }) => {
                    const priority = row.original.priority;
                    const config = priorityConfig[priority];
                    return (
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild disabled={!canEdit}>
                                <Button variant="ghost" className="h-8 px-2">
                                    <Badge variant={config.variant}>{getPriorityLabel(priority, t)}</Badge>
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="start">
                                {Object.entries(priorityConfig).map(([key, value]) => (
                                    <DropdownMenuItem
                                        key={key}
                                        onClick={() => onPriorityChange(row.original.id, key as TaskPriority)}
                                    >
                                        <Badge variant={value.variant} className="mr-2">
                                            {getPriorityLabel(key as TaskPriority, t)}
                                        </Badge>
                                        {priority === key && <span className="ml-auto">✓</span>}
                                    </DropdownMenuItem>
                                ))}
                            </DropdownMenuContent>
                        </DropdownMenu>
                    );
                },
                size: 100,
            },
            // Assignees column
            {
                accessorKey: 'assignees',
                header: () => (
                    <div className="flex items-center gap-2">
                        <User className="h-4 w-4" />
                        {t('tasks.assignees')}
                    </div>
                ),
                cell: ({ row }) => {
                    const task = row.original;
                    const assignees = task.assignees;
                    const assigneeIds = assignees.map(a => a.userId);

                    return (
                        <Popover>
                            <PopoverTrigger asChild>
                                <button className="flex items-center min-h-[28px] cursor-pointer hover:bg-surface-hover rounded px-1 py-0.5">
                                    {assignees.length === 0 ? (
                                        <span className="text-foreground-muted text-sm">{t('common.unassigned')}</span>
                                    ) : (
                                        <div className="flex -space-x-2">
                                            {assignees.slice(0, 3).map((assignee) => (
                                                <Avatar
                                                    key={assignee.id}
                                                    className="h-7 w-7 border-2 border-surface"
                                                    title={assignee.user.name}
                                                >
                                                    <AvatarImage src={assignee.user.avatarUrl || undefined} />
                                                    <AvatarFallback className="text-[10px] bg-primary text-primary-foreground">
                                                        {getInitials(assignee.user.name)}
                                                    </AvatarFallback>
                                                </Avatar>
                                            ))}
                                            {assignees.length > 3 && (
                                                <div className="h-7 w-7 rounded-full bg-surface-hover border-2 border-surface flex items-center justify-center text-xs text-foreground-muted">
                                                    +{assignees.length - 3}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </button>
                            </PopoverTrigger>
                            <PopoverContent className="w-64 p-2" align="start" onOpenAutoFocus={(e) => e.preventDefault()}>
                                <div className="space-y-1 max-h-64 overflow-y-auto">
                                    <p className="text-xs font-medium text-foreground-muted px-2 py-1">{t('tasks.selectAssignees')}</p>
                                    {projectMembers.map((member) => (
                                        <div
                                            key={member.userId}
                                            className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-surface-hover cursor-pointer"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                if (!onAssigneeChange) return;
                                                const isChecked = assigneeIds.includes(member.userId);
                                                const newAssignees = isChecked
                                                    ? assigneeIds.filter(id => id !== member.userId)
                                                    : [...assigneeIds, member.userId];
                                                onAssigneeChange(task.id, newAssignees);
                                            }}
                                        >
                                            <Checkbox
                                                checked={assigneeIds.includes(member.userId)}
                                                onCheckedChange={() => {
                                                    // Handled by parent div click
                                                }}
                                            />
                                            <Avatar className="h-6 w-6">
                                                <AvatarImage src={member.user.avatarUrl || undefined} />
                                                <AvatarFallback className="text-[10px] bg-primary text-primary-foreground">
                                                    {getInitials(member.user.name)}
                                                </AvatarFallback>
                                            </Avatar>
                                            <span className="text-sm truncate">{member.user.name}</span>
                                        </div>
                                    ))}
                                    {projectMembers.length === 0 && (
                                        <p className="text-sm text-foreground-muted text-center py-2">{t('taskDialog.noTeamMembers')}</p>
                                    )}
                                </div>
                            </PopoverContent>
                        </Popover>
                    );
                },
                size: 120,
            },
            // Due date column
            {
                accessorKey: 'dueDate',
                header: ({ column }) => (
                    <Button
                        variant="ghost"
                        onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
                        className="-ml-4 h-8 hover:bg-transparent"
                    >
                        <Calendar className="h-4 w-4 mr-2" />
                        {t('tasks.dueDate')}
                        {column.getIsSorted() === 'asc' ? (
                            <ChevronUp className="ml-2 h-4 w-4" />
                        ) : column.getIsSorted() === 'desc' ? (
                            <ChevronDown className="ml-2 h-4 w-4" />
                        ) : null}
                    </Button>
                ),
                cell: ({ row }) => {
                    const task = row.original;
                    const dueDate = task.dueDate;
                    const overdue = isOverdue(dueDate) && task.status !== 'DONE';
                    const formatted = formatDate(dueDate);

                    return (
                        <Popover>
                            <PopoverTrigger asChild>
                                <button
                                    className={cn(
                                        'text-sm cursor-pointer hover:bg-surface-hover rounded px-1 py-0.5',
                                        overdue ? 'text-red-400 font-medium' : 'text-foreground-secondary'
                                    )}
                                >
                                    {formatted || <span className="text-foreground-muted">—</span>}
                                </button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-3" align="start">
                                <div className="space-y-2">
                                    <p className="text-xs font-medium text-foreground-muted">Set due date</p>
                                    <Input
                                        type="date"
                                        value={dueDate ? dueDate.split('T')[0] : ''}
                                        onChange={(e) => {
                                            if (!onDueDateChange) return;
                                            const newDate = e.target.value
                                                ? new Date(e.target.value).toISOString()
                                                : null;
                                            onDueDateChange(task.id, newDate);
                                        }}
                                        className="w-full"
                                    />
                                    {dueDate && (
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            className="w-full text-foreground-muted"
                                            onClick={() => onDueDateChange?.(task.id, null)}
                                        >
                                            Clear date
                                        </Button>
                                    )}
                                </div>
                            </PopoverContent>
                        </Popover>
                    );
                },
                size: 110,
            },
            // Actions column
            {
                id: 'actions',
                cell: ({ row }) => {
                    const task = row.original;
                    return (
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild disabled={!canEdit}>
                                <Button variant="ghost" className="h-8 w-8 p-0">
                                    <span className="sr-only">Open menu</span>
                                    <MoreHorizontal className="h-4 w-4" />
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => onEditTask(task)}>
                                    <Pencil className="h-4 w-4 mr-2" />
                                    {t('common.edit')}
                                </DropdownMenuItem>
                                {(onQuickCreate || onInlineCreate) && canEdit && (
                                    <DropdownMenuItem onClick={() => setAddingSubtaskTo(task.id)}>
                                        <Plus className="h-4 w-4 mr-2" />
                                        {t('tasks.addSubtask')}
                                    </DropdownMenuItem>
                                )}
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                    onClick={() => onDeleteTask(task.id)}
                                    className="text-destructive focus:text-destructive"
                                >
                                    <Trash2 className="h-4 w-4 mr-2" />
                                    {t('common.delete')}
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    );
                },
                size: 50,
            },
            // Add custom field columns
            ...customFieldColumns,
        ],
        [onEditTask, onDeleteTask, onStatusChange, onPriorityChange, expandedTasks, onTaskClick, projectMembers, onAssigneeChange, onDueDateChange, customFieldColumns, onTitleChange, canEdit, onQuickCreate, onInlineCreate, addingSubtaskTo, setAddingSubtaskTo, newTaskTitle, setNewTaskTitle]
    );

    const table = useReactTable({
        data: tasks,
        columns,
        state: {
            sorting,
            columnFilters,
            columnVisibility,
            columnOrder: internalColumnOrder,
            rowSelection,
        },
        enableRowSelection: true,
        onRowSelectionChange: setRowSelection,
        onSortingChange: setSorting,
        onColumnFiltersChange: setColumnFilters,
        onColumnVisibilityChange: setColumnVisibility,
        onColumnOrderChange: setInternalColumnOrder,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
    });
    const visibleColumnCount = table.getVisibleLeafColumns().length;
    const tableMinWidth = table.getTotalSize();

    // R5 fix: detect when the table needs horizontal overflow and
    // sync the sticky scrollbar's scrollLeft with the table. The
    // sticky bar's spacer div width = `tableMinWidth` (set inline
    // in the JSX), so its scrollWidth matches the table's. The
    // ResizeObserver catches column visibility changes and window
    // resizes.
    useEffect(() => {
        const tableEl = tableScrollbarRef.current;
        const stickyEl = stickyScrollbarRef.current;
        if (!tableEl) return;

        const update = () => {
            const overflows = tableEl.scrollWidth > tableEl.clientWidth + 1;
            setShowStickyTop(overflows);
            // Sync the bar's scrollLeft with the table whenever the
            // table resizes (e.g. column visibility toggle).
            if (overflows && stickyEl) {
                stickyEl.scrollLeft = tableEl.scrollLeft;
            }
        };

        update();
        const ro = new ResizeObserver(update);
        ro.observe(tableEl);
        return () => ro.disconnect();
    }, [tableMinWidth, tasks.length]);

    // Sync external selection state
    useEffect(() => {
        if (onSelectionChange) {
            const selectedIds = Object.keys(rowSelection).filter((key) => rowSelection[key]);
            const taskIds = selectedIds.map((index) => tasks[parseInt(index)]?.id).filter(Boolean);
            onSelectionChange(taskIds as string[]);
        }
    }, [rowSelection, tasks, onSelectionChange]);

    // Get selected task IDs
    const selectedTaskIds = useMemo(() => {
        const selectedIds = Object.keys(rowSelection).filter((key) => rowSelection[key]);
        return selectedIds.map((index) => tasks[parseInt(index)]?.id).filter(Boolean) as string[];
    }, [rowSelection, tasks]);

    // Bulk actions state
    const [bulkActionLoading, setBulkActionLoading] = useState(false);

    // Track last selected index for shift-click selection
    const [lastSelectedIndex, setLastSelectedIndex] = useState<number | null>(null);

    // Track mouse down for drag selection
    const [isMouseDown, setIsMouseDown] = useState(false);
    const [dragStartIndex, setDragStartIndex] = useState<number | null>(null);
    const [dragMode, setDragMode] = useState<'select' | 'deselect' | null>(null);

    const handleBulkDelete = useCallback(async () => {
        if (!onBulkDelete || selectedTaskIds.length === 0) return;
        setBulkActionLoading(true);
        try {
            await onBulkDelete(selectedTaskIds);
            setRowSelection({});
        } finally {
            setBulkActionLoading(false);
        }
    }, [onBulkDelete, selectedTaskIds]);

    const handleBulkStatusChange = useCallback(async (status: TaskStatus) => {
        if (!onBulkStatusChange || selectedTaskIds.length === 0) return;
        setBulkActionLoading(true);
        try {
            await onBulkStatusChange(selectedTaskIds, status);
        } finally {
            setBulkActionLoading(false);
        }
    }, [onBulkStatusChange, selectedTaskIds]);

    const handleBulkPriorityChange = useCallback(async (priority: TaskPriority) => {
        if (!onBulkPriorityChange || selectedTaskIds.length === 0) return;
        setBulkActionLoading(true);
        try {
            await onBulkPriorityChange(selectedTaskIds, priority);
        } finally {
            setBulkActionLoading(false);
        }
    }, [onBulkPriorityChange, selectedTaskIds]);

    const handleBulkAssigneeAdd = useCallback(async (userId: string) => {
        if (!onBulkAssigneeChange || selectedTaskIds.length === 0) return;
        setBulkActionLoading(true);
        try {
            // Add assignee to all selected tasks
            for (const taskId of selectedTaskIds) {
                const task = tasks.find(t => t.id === taskId);
                const currentAssignees = task?.assignees.map(a => a.userId) || [];
                if (!currentAssignees.includes(userId)) {
                    await onBulkAssigneeChange([taskId], [...currentAssignees, userId]);
                }
            }
        } finally {
            setBulkActionLoading(false);
        }
    }, [onBulkAssigneeChange, selectedTaskIds, tasks]);

    const handleBulkCustomFieldChange = useCallback(async (fieldId: string, value: unknown) => {
        if (!onBulkCustomFieldChange || selectedTaskIds.length === 0) return;
        setBulkActionLoading(true);
        try {
            await onBulkCustomFieldChange(selectedTaskIds, fieldId, value);
        } finally {
            setBulkActionLoading(false);
        }
    }, [onBulkCustomFieldChange, selectedTaskIds]);

    if (isLoading) {
        return (
            <div className="space-y-3">
                {[...Array(5)].map((_, i) => (
                    <div
                        key={i}
                        className="h-14 bg-surface-hover rounded-lg animate-pulse"
                    />
                ))}
            </div>
        );
    }

    return (
        <div className="space-y-2">
            {/* Bulk action bar */}
            {selectedTaskIds.length > 0 && canEdit && (
                <div className="flex flex-wrap items-center gap-2 sm:gap-3 p-2 sm:p-3 bg-primary/10 border border-primary/20 rounded-lg">
                    <span className="text-sm font-medium text-foreground">
                        {selectedTaskIds.length} {t('tasks.task')}{selectedTaskIds.length !== 1 ? 's' : ''} {t('tasks.selected')}
                    </span>
                    <div className="flex flex-wrap items-center gap-2 ml-auto">
                        {onBulkStatusChange && (
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild disabled={!canEdit}>
                                    <Button variant="outline" size="sm" disabled={bulkActionLoading}>
                                        {t('tasks.setStatus')}
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="start">
                                    {Object.entries(statusConfig).map(([key, config]) => (
                                        <DropdownMenuItem
                                            key={key}
                                            onClick={() => handleBulkStatusChange(key as TaskStatus)}
                                        >
                                            <Badge variant={config.variant} className="mr-2">
                                                {getStatusLabel(key as TaskStatus, t)}
                                            </Badge>
                                        </DropdownMenuItem>
                                    ))}
                                </DropdownMenuContent>
                            </DropdownMenu>
                        )}

                        {/* Priority change */}
                        {onBulkPriorityChange && (
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild disabled={!canEdit}>
                                    <Button variant="outline" size="sm" disabled={bulkActionLoading}>
                                        {t('tasks.setPriority')}
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="start">
                                    {Object.entries(priorityConfig).map(([key, config]) => (
                                        <DropdownMenuItem
                                            key={key}
                                            onClick={() => handleBulkPriorityChange(key as TaskPriority)}
                                        >
                                            <Badge variant={config.variant} className="mr-2">
                                                {getPriorityLabel(key as TaskPriority, t)}
                                            </Badge>
                                        </DropdownMenuItem>
                                    ))}
                                </DropdownMenuContent>
                            </DropdownMenu>
                        )}

                        {/* Assign to */}
                        {onBulkAssigneeChange && projectMembers.length > 0 && (
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild disabled={!canEdit}>
                                    <Button variant="outline" size="sm" disabled={bulkActionLoading}>
                                        <User className="h-4 w-4 mr-1" />
                                        {t('tasks.assignTo')}
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="start" className="max-h-64 overflow-y-auto">
                                    {projectMembers.map((member) => (
                                        <DropdownMenuItem
                                            key={member.userId}
                                            onClick={() => handleBulkAssigneeAdd(member.userId)}
                                        >
                                            <Avatar className="h-6 w-6 mr-2">
                                                <AvatarImage src={member.user.avatarUrl || undefined} />
                                                <AvatarFallback className="text-xs">
                                                    {getInitials(member.user.name)}
                                                </AvatarFallback>
                                            </Avatar>
                                            {member.user.name}
                                        </DropdownMenuItem>
                                    ))}
                                </DropdownMenuContent>
                            </DropdownMenu>
                        )}

                        {/* Custom Fields bulk edit */}
                        {onBulkCustomFieldChange && customFields.length > 0 && (
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild disabled={!canEdit}>
                                    <Button variant="outline" size="sm" disabled={bulkActionLoading}>
                                        <Settings className="h-4 w-4 mr-1" />
                                        {t('tasks.customFields')}
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="start" className="w-48 max-h-64 overflow-y-auto">
                                    {customFields.map((field) => (
                                        <DropdownMenu key={field.id}>
                                            <DropdownMenuTrigger className="w-full">
                                                <DropdownMenuItem className="cursor-pointer flex justify-between">
                                                    <span className="truncate">{field.name}</span>
                                                    <ChevronRight className="h-4 w-4 ml-2" />
                                                </DropdownMenuItem>
                                            </DropdownMenuTrigger>
                                            <DropdownMenuContent side="right" align="start" className="w-48">
                                                {field.type === 'SELECT' && field.options?.map((option) => (
                                                    <DropdownMenuItem
                                                        key={option.id}
                                                        onClick={() => handleBulkCustomFieldChange(field.id, option.id)}
                                                    >
                                                        <div
                                                            className="h-3 w-3 rounded-full mr-2"
                                                            style={{ backgroundColor: option.color || '#6b7280' }}
                                                        />
                                                        {option.label}
                                                    </DropdownMenuItem>
                                                ))}
                                                {field.type === 'CHECKBOX' && (
                                                    <>
                                                        <DropdownMenuItem onClick={() => handleBulkCustomFieldChange(field.id, true)}>
                                                            ✓ {t('tasks.checked')}
                                                        </DropdownMenuItem>
                                                        <DropdownMenuItem onClick={() => handleBulkCustomFieldChange(field.id, false)}>
                                                            ○ {t('tasks.unchecked')}
                                                        </DropdownMenuItem>
                                                    </>
                                                )}
                                                {field.type === 'PERSON' && projectMembers.map((member) => (
                                                    <DropdownMenuItem
                                                        key={member.userId}
                                                        onClick={() => handleBulkCustomFieldChange(field.id, member.userId)}
                                                    >
                                                        <Avatar className="h-5 w-5 mr-2">
                                                            <AvatarImage src={member.user.avatarUrl || undefined} />
                                                            <AvatarFallback className="text-xs">
                                                                {getInitials(member.user.name)}
                                                            </AvatarFallback>
                                                        </Avatar>
                                                        {member.user.name}
                                                    </DropdownMenuItem>
                                                ))}
                                                {(field.type === 'TEXT' || field.type === 'NUMBER' || field.type === 'URL' || field.type === 'EMAIL' || field.type === 'DATE') && (
                                                    <DropdownMenuItem disabled className="text-foreground-muted text-xs">
                                                        {t('tasks.editIndividually')}
                                                    </DropdownMenuItem>
                                                )}
                                                {field.type === 'MULTI_SELECT' && (
                                                    <>
                                                        <div className="px-2 py-1.5 text-xs text-foreground-muted font-medium">
                                                            {t('tasks.toggleOptions')}
                                                        </div>
                                                        {field.options?.map((option) => {
                                                            // Calculate if this option is selected for all selected tasks
                                                            const selectedTaskIds = Object.entries(rowSelection)
                                                                .filter(([_, selected]) => selected)
                                                                .map(([id]) => id);

                                                            const tasksWithOption = selectedTaskIds.filter(taskId => {
                                                                const task = tasks.find(t => t.id === taskId);
                                                                const fieldValues = task?.customFieldValues?.[field.id];
                                                                return Array.isArray(fieldValues) && fieldValues.includes(option.id);
                                                            });

                                                            const allHaveOption = tasksWithOption.length === selectedTaskIds.length;
                                                            const someHaveOption = tasksWithOption.length > 0 && tasksWithOption.length < selectedTaskIds.length;

                                                            return (
                                                                <DropdownMenuCheckboxItem
                                                                    key={option.id}
                                                                    checked={allHaveOption}
                                                                    className={cn(
                                                                        "flex items-center gap-2",
                                                                        someHaveOption && "opacity-70"
                                                                    )}
                                                                    onSelect={(e) => e.preventDefault()}
                                                                    onCheckedChange={() => handleBulkCustomFieldChange(field.id, { toggleOption: option.id })}
                                                                >
                                                                    <div
                                                                        className="h-2.5 w-2.5 rounded-full"
                                                                        style={{ backgroundColor: option.color || '#6b7280' }}
                                                                    />
                                                                    <span>{option.label}</span>
                                                                    {someHaveOption && (
                                                                        <span className="ml-auto text-xs text-foreground-muted">
                                                                            ({tasksWithOption.length}/{selectedTaskIds.length})
                                                                        </span>
                                                                    )}
                                                                </DropdownMenuCheckboxItem>
                                                            );
                                                        })}
                                                    </>
                                                )}
                                                <DropdownMenuSeparator />
                                                <DropdownMenuItem
                                                    onClick={() => handleBulkCustomFieldChange(field.id, null)}
                                                    className="text-foreground-muted"
                                                >
                                                    {t('tasks.clearValue')}
                                                </DropdownMenuItem>
                                            </DropdownMenuContent>
                                        </DropdownMenu>
                                    ))}
                                </DropdownMenuContent>
                            </DropdownMenu>
                        )}

                        {/* Delete */}
                        {onBulkDelete && (
                            <Button
                                variant="destructive"
                                size="sm"
                                onClick={handleBulkDelete}
                                disabled={bulkActionLoading}
                            >
                                {bulkActionLoading ? (
                                    <Loader2 className="h-4 w-4 animate-spin mr-1" />
                                ) : (
                                    <Trash2 className="h-4 w-4 mr-1" />
                                )}
                                {t('common.delete')}
                            </Button>
                        )}

                        {/* Clear selection */}
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setRowSelection({})}
                            disabled={bulkActionLoading}
                        >
                            <X className="h-4 w-4" />
                        </Button>
                    </div>
                </div>
            )}

            {/* Column management toolbar */}
            <div className="flex justify-end">
                <DropdownMenu open={showColumnMenu} onOpenChange={setShowColumnMenu}>
                    <DropdownMenuTrigger asChild disabled={!canEdit}>
                        <Button variant="outline" size="sm" className="h-8 gap-2">
                            <Columns className="h-4 w-4" />
                            {t('tasks.columns')}
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-48">
                        {defaultColumnIds
                            .filter(id => id !== 'select' && id !== 'actions')
                            .map(columnId => {
                                const isVisible = columnVisibility[columnId] !== false;
                                return (
                                    <DropdownMenuItem
                                        key={columnId}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            toggleColumnVisibility(columnId);
                                        }}
                                        className="cursor-pointer"
                                    >
                                        <div className="flex items-center gap-2 flex-1">
                                            {isVisible ? (
                                                <Eye className="h-4 w-4 text-green-500" />
                                            ) : (
                                                <EyeOff className="h-4 w-4 text-foreground-muted" />
                                            )}
                                            <span className={cn(!isVisible && 'text-foreground-muted')}>
                                                {getColumnName(columnId)}
                                            </span>
                                        </div>
                                    </DropdownMenuItem>
                                );
                            })}
                    </DropdownMenuContent>
                </DropdownMenu>
            </div>

            <div className="rounded-lg border border-border overflow-hidden sm:overflow-visible">
                {/* R6 fix: sticky horizontal scrollbar is now a SIBLING
                    of the table scroll container (NOT inside it).
                    Reason: position:sticky is constrained to the
                    nearest scrolling ancestor. When the bar was inside
                    the table container, it stuck to that container's
                    top, which scrolls WITH the page — so it didn't
                    actually stay pinned to the viewport. Outside the
                    table container, sticky top-0 finds the html/body
                    scrolling ancestor and pins to viewport-top.
                    
                    The bar's spacer div width = `tableMinWidth`, so
                    scrollWidth matches the table's → dragging the bar
                    scrolls the table. 2-way onScroll sync wires it.
                    
                    We also dropped `overflow-hidden` from the wrapper
                    (kept it only on small viewports where there's no
                    sticky behavior to worry about) because
                    overflow-hidden clips sticky elements.
                    
                    z-30 keeps the bar above the page header on
                    mobile where the header is at top-0 too. */}
                {showStickyTop && (
                    <div
                        className="task-table-scrollbar-shell sticky top-0 z-30 border-b border-border/80 px-2 py-1 cursor-pointer bg-surface/95 backdrop-blur-md"
                        role="region"
                        aria-label={t('table.horizontalScroll')}
                        tabIndex={0}
                    >
                        {/* R7 fix: the ref + onScroll MUST be on the
                            actually-scrolling element (the inner div
                            with `overflow-x-scroll`). R6 wired them
                            to the outer wrapper, which doesn't
                            scroll — its `scrollLeft` is always 0,
                            so syncing set the table's scrollLeft to
                            0 every time. Move both to the inner div. */}
                        <div
                            ref={stickyScrollbarRef}
                            className="task-table-scrollbar overflow-x-scroll overscroll-x-contain"
                            onScroll={() => syncHorizontalScroll('sticky')}
                        >
                            <div
                                className="h-1 min-w-full"
                                style={{ width: tableMinWidth }}
                                aria-hidden="true"
                            />
                        </div>
                    </div>
                )}
                <div
                    ref={tableScrollbarRef}
                    className="task-table-scrollbar overflow-x-auto overscroll-x-contain"
                    onScroll={() => syncHorizontalScroll('table')}
                >

                    <table
                        className="w-full table-fixed"
                        style={{ minWidth: tableMinWidth }}
                    >
                        <colgroup>
                            {table.getVisibleLeafColumns().map((column) => (
                                <col key={column.id} style={{ width: column.getSize() }} />
                            ))}
                        </colgroup>
                        <thead className="bg-surface-hover">
                            {table.getHeaderGroups().map((headerGroup) => (
                                <tr key={headerGroup.id}>
                                    {headerGroup.headers.map((header) => {
                                        const canDrag = header.id !== 'select' && header.id !== 'actions';
                                        return (
                                            <th
                                                key={header.id}
                                                style={{ width: header.getSize() }}
                                                className={cn(
                                                    "h-10 px-2 sm:px-3 md:px-4 text-left align-middle font-medium text-foreground-muted text-xs sm:text-sm",
                                                    canDrag && "cursor-grab",
                                                    draggedColumn === header.id && "opacity-50 bg-primary/20"
                                                )}
                                                draggable={canDrag}
                                                onDragStart={(e) => canDrag && handleDragStart(e, header.id)}
                                                onDragOver={handleDragOver}
                                                onDrop={(e) => handleDrop(e, header.id)}
                                                onDragEnd={handleDragEnd}
                                            >
                                                <div className="flex items-center gap-1">
                                                    {canDrag && (
                                                        <GripVertical className="h-3 w-3 text-foreground-muted/50 opacity-0 group-hover:opacity-100 transition-opacity" />
                                                    )}
                                                    {header.isPlaceholder
                                                        ? null
                                                        : flexRender(
                                                            header.column.columnDef.header,
                                                            header.getContext()
                                                        )}
                                                </div>
                                            </th>
                                        );
                                    })}
                                </tr>
                            ))}
                        </thead>
                        <tbody
                            onMouseLeave={() => {
                                setIsMouseDown(false);
                                setDragStartIndex(null);
                                setDragMode(null);
                            }}
                            onMouseUp={() => {
                                setIsMouseDown(false);
                                setDragStartIndex(null);
                                setDragMode(null);
                            }}
                            className={isMouseDown ? 'select-none' : ''}
                        >
                            {table.getRowModel().rows?.length ? (
                                table.getRowModel().rows.map((row, rowIndex) => {
                                    const task = row.original;
                                    const isExpanded = expandedTasks.has(task.id);

                                    // Handle selection with shift-click support
                                    const handleRowSelect = (e: React.MouseEvent) => {
                                        // Check if clicking on interactive elements (excluding checkbox in selection cell)
                                        const target = e.target as HTMLElement;
                                        const clickedCell = target.closest('td');
                                        const isSelectionCell = clickedCell?.cellIndex === 0;

                                        // Don't handle if clicking on interactive elements (except selection cell checkbox)
                                        if (!isSelectionCell) {
                                            const isInteractive = target.closest('button, input, a, [role="menuitem"], [data-radix-collection-item], select');
                                            if (isInteractive) return;
                                        }

                                        // If shift key is pressed and we have a last selected index, select range
                                        if (e.shiftKey && lastSelectedIndex !== null && isSelectionCell) {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            const start = Math.min(lastSelectedIndex, rowIndex);
                                            const end = Math.max(lastSelectedIndex, rowIndex);
                                            const newSelection: Record<string, boolean> = { ...rowSelection };

                                            for (let i = start; i <= end; i++) {
                                                newSelection[i.toString()] = true;
                                            }

                                            setRowSelection(newSelection);
                                            // Keep lastSelectedIndex as is for further shift selections
                                        }
                                        // If clicking in the selection cell, toggle selection
                                        else if (isSelectionCell) {
                                            e.stopPropagation();
                                            row.toggleSelected();
                                            setLastSelectedIndex(rowIndex);
                                        }
                                        // Otherwise open task detail
                                        else if (onTaskClick) {
                                            onTaskClick(task);
                                        }
                                    };

                                    // Handle mouse enter for drag selection
                                    const handleMouseEnter = () => {
                                        if (isMouseDown && dragStartIndex !== null && dragMode) {
                                            const start = Math.min(dragStartIndex, rowIndex);
                                            const end = Math.max(dragStartIndex, rowIndex);

                                            const newSelection: Record<string, boolean> = { ...rowSelection };

                                            for (let i = start; i <= end; i++) {
                                                if (dragMode === 'select') {
                                                    newSelection[i.toString()] = true;
                                                } else {
                                                    delete newSelection[i.toString()];
                                                }
                                            }

                                            setRowSelection(newSelection);
                                        }
                                    };

                                    const handleMouseDown = (e: React.MouseEvent) => {
                                        const target = e.target as HTMLElement;
                                        const clickedCell = target.closest('td');
                                        if (clickedCell?.cellIndex === 0) {
                                            setIsMouseDown(true);
                                            setDragStartIndex(rowIndex);
                                            // Set drag mode based on whether this row is currently selected
                                            setDragMode(row.getIsSelected() ? 'deselect' : 'select');
                                        }
                                    };

                                    return (
                                        <React.Fragment key={row.id}>
                                            <tr
                                                data-state={row.getIsSelected() && 'selected'}
                                                className="border-t border-border hover:bg-surface-hover/50 transition-colors data-[state=selected]:bg-primary/10 cursor-pointer"
                                                onClick={handleRowSelect}
                                                onMouseEnter={handleMouseEnter}
                                                onMouseDown={handleMouseDown}
                                            >
                                                {row.getVisibleCells().map((cell) => (
                                                    <td
                                                        key={cell.id}
                                                        style={{ width: cell.column.getSize() }}
                                                        className="overflow-hidden px-2 sm:px-3 md:px-4 py-2 sm:py-3 align-top"
                                                    >
                                                        {flexRender(
                                                            cell.column.columnDef.cell,
                                                            cell.getContext()
                                                        )}
                                                    </td>
                                                ))}
                                            </tr>
                                            {/* Inline add subtask row */}
                                            {addingSubtaskTo === task.id && (
                                                <tr className="border-t border-border bg-surface-hover/20">
                                                    <td colSpan={visibleColumnCount} className="px-4 py-2">
                                                        <div className="pl-6">
                                                            {onQuickCreate ? (
                                                                <QuickAddTaskPopover
                                                                    onSubmit={async (data) => {
                                                                        await onQuickCreate({
                                                                            ...data,
                                                                            parentId: task.id,
                                                                            customFieldValues: data.customFieldValues,
                                                                        });
                                                                        setAddingSubtaskTo(null);
                                                                    }}
                                                                    projectMembers={projectMembers}
                                                                    parentId={task.id}
                                                                    isSubtask
                                                                    defaultExpanded
                                                                    onCancel={() => setAddingSubtaskTo(null)}
                                                                    customFields={customFields}
                                                                    filterDefaults={filterDefaults}
                                                                />
                                                            ) : onInlineCreate ? (
                                                                <div className="flex items-center gap-2">
                                                                    <Input
                                                                        value={newTaskTitle}
                                                                        onChange={(e) => setNewTaskTitle(e.target.value)}
                                                                        onKeyDown={(e) => {
                                                                            if (e.key === 'Enter') {
                                                                                e.preventDefault();
                                                                                onInlineCreate(newTaskTitle, task.id);
                                                                                setNewTaskTitle('');
                                                                                setAddingSubtaskTo(null);
                                                                            } else if (e.key === 'Escape') {
                                                                                setNewTaskTitle('');
                                                                                setAddingSubtaskTo(null);
                                                                            }
                                                                        }}
                                                                        placeholder={t('tasks.enterSubtaskTitle')}
                                                                        className="flex-1 h-9"
                                                                        autoFocus
                                                                    />
                                                                    <Button
                                                                        size="sm"
                                                                        onClick={() => {
                                                                            onInlineCreate(newTaskTitle, task.id);
                                                                            setNewTaskTitle('');
                                                                            setAddingSubtaskTo(null);
                                                                        }}
                                                                        disabled={!newTaskTitle.trim()}
                                                                    >
                                                                        {t('common.add')}
                                                                    </Button>
                                                                    <Button
                                                                        size="sm"
                                                                        variant="ghost"
                                                                        onClick={() => {
                                                                            setNewTaskTitle('');
                                                                            setAddingSubtaskTo(null);
                                                                        }}
                                                                    >
                                                                        {t('common.cancel')}
                                                                    </Button>
                                                                </div>
                                                            ) : null}
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                            {/* Subtask rows */}
                                            {task.subtaskCount > 0 && (
                                                <SubtaskRows
                                                    projectId={projectId}
                                                    parentTaskId={task.id}
                                                    isExpanded={isExpanded}
                                                    depth={1}
                                                    columnCount={visibleColumnCount}
                                                    onEditTask={onEditTask}
                                                    onDeleteTask={onDeleteTask}
                                                    onStatusChange={onStatusChange}
                                                    onPriorityChange={onPriorityChange}
                                                    onTaskClick={onTaskClick}
                                                    projectMembers={projectMembers}
                                                    onAssigneeChange={onAssigneeChange}
                                                    expandedTasks={expandedTasks}
                                                    setExpandedTasks={setExpandedTasks}
                                                    canEdit={canEdit}
                                                    onInlineCreate={onInlineCreate}
                                                    onQuickCreate={onQuickCreate}
                                                    customFields={customFields}
                                                    onTitleChange={onTitleChange}
                                                    filterDefaults={filterDefaults}
                                                />
                                            )}
                                        </React.Fragment>
                                    );
                                })
                            ) : !isAddingTask ? (
                                <tr>
                                    <td
                                        colSpan={visibleColumnCount}
                                        className="h-24 text-center text-foreground-muted"
                                    >
                                        {t('tasks.noTasksFound')}
                                    </td>
                                </tr>
                            ) : null}

                            {/* Inline add task row */}
                            {canEdit && (onQuickCreate || onInlineCreate) && (
                                <tr className="border-t border-border hover:bg-surface-hover/30 transition-colors group">
                                    <td className="px-4 py-2" colSpan={visibleColumnCount}>
                                        {onQuickCreate ? (
                                            <QuickAddTaskPopover
                                                onSubmit={async (data) => {
                                                    await onQuickCreate({
                                                        ...data,
                                                        customFieldValues: data.customFieldValues,
                                                    });
                                                }}
                                                projectMembers={projectMembers}
                                                customFields={customFields}
                                                filterDefaults={filterDefaults}
                                            />
                                        ) : (
                                            <>
                                                {isAddingTask ? (
                                                    <div className="flex items-center gap-2">
                                                        <Input
                                                            ref={inputRef}
                                                            value={newTaskTitle}
                                                            onChange={(e) => setNewTaskTitle(e.target.value)}
                                                            onKeyDown={handleKeyDown}
                                                            placeholder={t('tasks.enterTitle')}
                                                            className="flex-1 h-9"
                                                            autoFocus
                                                            disabled={isCreating}
                                                        />
                                                        <Button
                                                            size="sm"
                                                            onClick={handleInlineCreate}
                                                            disabled={!newTaskTitle.trim() || isCreating}
                                                        >
                                                            {isCreating ? (
                                                                <Loader2 className="h-4 w-4 animate-spin" />
                                                            ) : (
                                                                t('common.add')
                                                            )}
                                                        </Button>
                                                        <Button
                                                            size="sm"
                                                            variant="ghost"
                                                            onClick={() => {
                                                                setIsAddingTask(false);
                                                                setNewTaskTitle('');
                                                            }}
                                                            disabled={isCreating}
                                                        >
                                                            {t('common.cancel')}
                                                        </Button>
                                                    </div>
                                                ) : (
                                                    <button
                                                        onClick={() => setIsAddingTask(true)}
                                                        className="flex items-center gap-2 text-foreground-muted hover:text-foreground transition-colors"
                                                    >
                                                        <Plus className="h-4 w-4" />
                                                        <span className="text-sm">{t('tasks.addTask')}</span>
                                                    </button>
                                                )}
                                            </>
                                        )}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
